import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase, logActivity } from '../lib/supabase';
import { useConfig } from '../contexts/ConfigContext';
import { useAuth } from '../contexts/AuthContext';
import AddendumsModal from '../components/AddendumsModal';
import ServiceOrderPreviewModal from '../components/ServiceOrderPreviewModal';
import EngagementPanel from '../components/EngagementPanel';
// v3.5.99.7.13: ETF Calculator (per-quote)
import ETFCalculatorModal from '../components/ETFCalculatorModal';
import { loadAddendumTemplates, validateAddendums } from '../lib/addendums';
// v3.5.67: per-user quote-type permissions
import { userCanCreateQuoteType } from '../lib/quotePermissions';
import { calcQuote, lookupZip, fmt$, fmt$0, fmtPct, gmColor, gmBg, getBurdenedRate, getSeniorBurdenedRate } from '../lib/pricing';
import { loadPackageCostComponents } from '../lib/packageCostComponents';
import { calcVoice, calcBundleDiscount, FAX_PACKAGES, CX_TIERS, getRecommendedTier, getFaxPackages, loadVoiceHardwareCatalog, loadVoiceCXTiers, getBundleVoiceDiscOptions } from '../lib/voicePricing';
// v3.5.92.2: unified totals utility — Bundle migration (Phase 1 item 2, 3/4).
// See CLAUDE.md §10b. §4a: picker base on Bundle is now IT onboarding +
// Voice Programming&Config only (NOT the pre-deploy combinedNRC which
// included voice hardware / porting / activation).
import { buildTotals } from '../lib/quoteTotals';
import { writeQuoteUrlToDeal, searchDeals, getDealFull, updateDealDescription, captureHubspotSnapshot, pushHubspotGapFill, pushHubspotOverwrite } from '../lib/hubspot';
import QuoteNotes    from '../components/QuoteNotes';
import QuoteHistory  from '../components/QuoteHistory';
import SignatureCard from '../components/SignatureCard';
import { saveQuoteVersion } from '../lib/quoteVersions';
import { ReviewBanner } from '../components/SendForReview';
import QuoteSaveBar from '../components/QuoteSaveBar';
import RepSelector from '../components/RepSelector';
import ThreatProfileInfo from '../components/ThreatProfileInfo';
import DomainsInfo from '../components/DomainsInfo';
import FlexTimeMeter   from '../components/FlexTimeMeter';
import FlexTimeSelector from '../components/FlexTimeSelector';
import { calcFlexBlock } from '../lib/flexTime';
import { DocumentsPanel } from '../components/RateSheetModal';
import OnboardingIncentive from '../components/OnboardingIncentive';
import ManualDiscounts from '../components/ManualDiscounts';
import OneTimeDiscounts from '../components/OneTimeDiscounts';
import AgreementEffectiveDate from '../components/AgreementEffectiveDate';
import { applyManualDiscounts } from '../lib/manualDiscounts';
import { applyOneTimeDiscounts } from '../lib/oneTimeDiscounts';
// v3.8.5 — Unified-UX workspace shell (flag ferrumiq_v3_bundle, default off).
import { WorkspaceShell, QuoteHeader, LiveTotalCard, RailCard, SubCard, Button as V3Button } from '../components/v3';
import { isQuoteLocked, QUOTE_LOCK_REASON } from '../lib/quoteLock';
import LockedQuoteBanner from '../components/LockedQuoteBanner';
import QuoteDocuments from '../components/QuoteDocuments';
import { v3 } from '../lib/styleTokens';
import HubSpotConnect from '../components/HubSpotConnect';
// v3.5.86.3: shared AssumptionsModal mounted with quoteType="bundle".
// Foundation refactor in v3.5.86.1 made the modal quote-type-aware; v3.5.86.2
// added multi-site capture; v3.5.86.3 adds the bundle (IT+Voice) capture
// flow + combined IT+Voice AI prompt.
import AssumptionsModal from '../components/AssumptionsModal';
import SPTConnect    from '../components/SPTConnect';
import MarketRateCard from '../components/MarketRateCard';
import MarketStatusBadge from '../components/MarketStatusBadge';
import { staticTierToMILabel, getOrAnalyzeMarket, getRateSheet, tierColor, tierBg } from '../lib/marketRates';

const DEF_IT = {
  users:0, sharedMailboxes:0, workstations:0, endpoints:0, mobileDevices:0,
  servers:0, locations:0, cloudTenants:0, vendors:0,
  // v3.5.96.0: domains as new IT infrastructure input. Same pattern as
  // QuotePage's DEF_INPUTS but nested under itInputs since Bundle stores
  // its IT-side inputs separately from the Voice-side. Persists to the
  // bundle quote's inputs.it.domains JSONB column on save.
  domains:0,
  requestedCoverage:'business_hours', compliance:'none',
  // v3.5.58: Industry Risk default is 'none' (no ITSO uplift); execReporting
  // removed — flat-rate Strategic Advisory products retired and the toggle
  // was its only remaining UI surface.
  industryRisk:'none', complexity:'low', contractTerm:24,
  selectedProducts:[],
};

const DEF_V = {
  quoteType:'hosted', licenseType:'pro',
  seats:0, seatPrice:0, seatCost:0,
  commonAreaPhones:0, voicemailOnly:0, doorPhones:0, pagingDevices:0, specialRingers:0,
  cxTierId:'pro_8', clientPaysMonthly:true, largerInstance:false,
  sipChannels:0,
  localDIDs:0, smsDIDs:0, tollFreeNumbers:0, e911DIDs:0, tollFreePerMin:false,
  faxType:'none', faxQty:1,
  callRecording:false,
  smsEnabled:false, smsNewRegistration:true, smsCampaigns:1,
  hardwareType:'none', hardwareModel:'T33G', hardwareQty:0,
  programmingFee:0, portingNumbers:0,
  internationalDialing:'none',
};

export default function BundleQuotePage() {
  const { id } = useParams();
  const { packages, products, marketTiers, settings, productsByCategory, exclusiveGroups, loading: configLoading } = useConfig();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location  = useLocation();

  // v3.5.67: quote-type permission gate (see QuotePage for full rationale).
  // v3.5.69: moved below useNavigate — see QuotePage v3.5.69 comment for full
  // explanation of the TDZ error this avoids.
  const _isNewQuote = !id;
  const _hasPermission = userCanCreateQuoteType(profile, 'bundle');
  const isReadOnly = !_isNewQuote && profile != null && !_hasPermission;
  useEffect(() => {
    if (_isNewQuote && profile != null && !_hasPermission) {
      navigate('/quotes', { replace: true, state: { permissionDenied: 'bundle' } });
    }
  }, [profile, _isNewQuote, _hasPermission, navigate]);

  const [showUnbundle, setShowUnbundle] = useState(false);
  const [unbundling,   setUnbundling]   = useState(false);
  const [faxPackagesDB, setFaxPackagesDB] = useState([]);
  // v3.5.23: voice hardware catalog from voice_hardware table
  const [hardwareCatalog, setHardwareCatalog] = useState({ yealink: [], atas: [] });
  // v3.5.77: 3CX license tier catalog from voice_cx_tiers table. Empty array
  // on mount; loadVoiceCXTiers() populates. calcVoice falls back to
  // CX_TIERS_BASE constant in voicePricing.js if this stays empty.
  const [cxTiersDB, setCxTiersDB] = useState([]);

  // ── Voice Fax Packages — DB-loaded once on mount, drives sell + cost ─────────
  useEffect(() => {
    supabase.from('voice_fax_packages').select('*').eq('active', true).order('sort_order')
      .then(({ data }) => setFaxPackagesDB(data || []));
  }, []);
  // ── Voice Hardware Catalog — DB-loaded once on mount (v3.5.23) ────────────────
  useEffect(() => {
    loadVoiceHardwareCatalog().then(setHardwareCatalog);
  }, []);
  // ── Voice CX License Tiers — DB-loaded once on mount (v3.5.77) ────────────────
  useEffect(() => {
    loadVoiceCXTiers().then(setCxTiersDB);
  }, []);


  // Shared client fields
  const [proposalName,     setProposalName]     = useState('');
  const [pricingSnapshot, setPricingSnapshot] = useState(null);
  const [acceptedRates,   setAcceptedRates]   = useState(null);  // locked-in market rates from MarketRateCard accept
  // ── v3.5.83.2.1: Lock-Rates-First UI guard (extends QuotePage v3.5.83.2) ──
  // Bundle already had `acceptedRates` state for FlexBlock rate hydration;
  // this guard piggybacks on that state for the visible warnings/modal.
  // Three coordinated surfaces:
  //   1. Yellow banner at top of right panel (when saved + unlocked + pre-snapshot)
  //   2. Yellow border + ⚠ on Send buttons inside BundleSignatureCard
  //   3. Confirmation modal — "Lock Rates Now" scrolls to id="oos-rate-card";
  //      "Send Anyway" calls signatureCardRef.openSendModal() to bypass.
  // Backend block at /netlify/functions/sendServiceOrder rejects unlocked
  // sends across all 4 quote types (HTTP 400 code:'rates_not_locked').
  const [showLockRatesModal, setShowLockRatesModal] = useState(null); // null | 'send_signature'
  const signatureCardRef = useRef(null);
  const [sptProposalId,   setSptProposalId]   = useState(null);
  const [priceLockDate,   setPriceLockDate]   = useState(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [repId,          setRepId]          = useState(null);
  const [obIncentive,    setObIncentive]    = useState(null);
  // v3.5.99.7.23: manual discount lines [{ type:'pct'|'fixed', value, note }]
  const [manualDiscounts, setManualDiscounts] = useState([]);
  const [oneTimeDiscounts, setOneTimeDiscounts] = useState([]);
  const [bundleFocusedSection, setBundleFocusedSection] = useState(null);  // v3.8.5 workspace
  // v3.5.99.7.24: optional back-dated agreement effective date (YYYY-MM-DD)
  const [effectiveDate, setEffectiveDate] = useState('');
  const [flexHours,      setFlexHours]      = useState(null);
  const [repProfile,     setRepProfile]     = useState(null);
  const [teamMembers,    setTeamMembers]    = useState([]);
  const [recipientBiz,     setRecipientBiz]     = useState('');
  const [recipientContact, setRecipientContact] = useState('');
  const [recipientEmail,   setRecipientEmail]   = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  // v3.5.99.7.3: client phone/mobile pulled from HubSpot in connectDeal.
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientMobilePhone, setRecipientMobilePhone] = useState('');
  // v3.5.99.7.4: HubSpot write-back state. contactId/companyId route
  // updateContact/updateCompany calls. snapshot captures HubSpot's original
  // values at connectDeal time so gap-fill knows which fields to push.
  const [hubContactId, setHubContactId] = useState('');
  const [hubCompanyId, setHubCompanyId] = useState('');
  const [hubspotSnapshot, setHubspotSnapshot] = useState(null);
  // v3.5.99.7.13: ETF Calculator modal visibility
  const [showEtfModal, setShowEtfModal] = useState(false);
  // v3.5.99.7.6: manual full-overwrite push state. Distinct from the
  // existing hubMsg/hubSyncing state (which is QuotePage-only and used
  // by the older syncToDeal button). hubPushing/hubPushMsg track the
  // new pushAllToHubspot flow.
  const [hubPushing, setHubPushing] = useState(false);
  const [hubPushMsg, setHubPushMsg] = useState('');

  // v3.5.99.7.6: manual full-overwrite push to HubSpot. Unlike the
  // automatic gap-fill (which only pushes fields HubSpot was originally
  // missing), this pushes ALL current Ferrum values, overwriting whatever
  // is in HubSpot. Use when rep wants to force HubSpot to match Ferrum's
  // current state — typically after correcting a phone number or address
  // in Ferrum that HubSpot has stale.
  async function pushAllToHubspot() {
    if (!hubContactId && !hubCompanyId) {
      setHubPushMsg('No HubSpot deal linked — connect a deal first.');
      return;
    }
    setHubPushing(true); setHubPushMsg('');
    try {
      const r = await pushHubspotOverwrite({
        contactId: hubContactId, companyId: hubCompanyId,
        recipientContact, recipientEmail, recipientPhone, recipientMobilePhone, recipientAddress,
      });
      if (r.updatedSnapshot) setHubspotSnapshot(r.updatedSnapshot);
      const total = r.contactFields.length + r.companyFields.length;
      if (total > 0) {
        setHubPushMsg(`✓ Pushed ${total} field${total > 1 ? 's' : ''} (${r.contactFields.length} contact, ${r.companyFields.length} company)`);
      } else {
        setHubPushMsg('Nothing to push — all recipient fields are empty.');
      }
      if (r.errors?.length) {
        console.warn('HubSpot push errors:', r.errors);
        setHubPushMsg(prev => prev + ' · ⚠ some errors (see console)');
      }
    } catch (e) {
      setHubPushMsg('✗ ' + (e.message || 'Push failed'));
    }
    setHubPushing(false);
  }
  // v3.5.99.0: per-quote override for proposal-approved notification CC list.
  const [notificationCc,   setNotificationCc]   = useState('');
  // v3.5.99.4: per-quote addendums — see QuotePage canonical block for rationale.
  const [addendums,         setAddendums]         = useState([]);
  const [addendumTemplates, setAddendumTemplates] = useState([]);
  const [addendumsModalOpen, setAddendumsModalOpen] = useState(false);
  // v3.5.99.5: Service Order preview modal — opens the full SO PDF inline so
  // reps can sanity-check the document before sending to SignWell. The modal
  // calls /.netlify/functions/buildSignatureDocument directly (same backend
  // path the actual send uses), so what's previewed is exactly what gets sent.
  const [soPreviewModalOpen, setSoPreviewModalOpen] = useState(false);
  useEffect(() => { loadAddendumTemplates().then(setAddendumTemplates); }, []);
  const [clientZip,        setClientZip]        = useState('');
  const [zipResult,        setZipResult]        = useState(null);
  const [zipApplied,       setZipApplied]       = useState(false);
  const [marketCity,       setMarketCity]       = useState('');
  const [marketState,      setMarketState]      = useState('');
  // v3.5.43: see QuotePage for rationale
  const [marketAnalysis,   setMarketAnalysis]   = useState(null);
  // v3.5.38: aiMultiplier flow on Bundle, symmetric with QuotePage. With tier
  // multipliers deactivated in v3.5.38, Bundle quotes need MI to drive market
  // adjustment — otherwise pricing has no geographic sensitivity at all.
  const [aiMultiplier,       setAiMultiplier]       = useState(null);
  const [aiMultiplierTier,   setAiMultiplierTier]   = useState(null);
  const [showMktRecommend,   setShowMktRecommend]   = useState(false);
  const [pendingMultiplier,  setPendingMultiplier]  = useState(null);
  const [dealDescription,  setDealDescription]  = useState('');
  const [quoteStatus,      setQuoteStatus]      = useState('draft');

  // v3.5.86.3: Customer Story & Assumptions modal state. Mirrors the
  // QuotePage v3.5.81.6 / MultiSiteQuotePage v3.5.86.2 patterns:
  //   - showAssumptions toggles the modal
  //   - assumptionsStory mirrors the saved story text for at-a-glance status
  //   - assumptionsSavedAt is the savedAt timestamp from the last save
  //   - assumptionsComplete drives the button color/icon (✓ green vs ⚠ amber)
  // Bundle quotes don't yet have a Send Proposal flow with a required gate
  // — like Multi-Site, the modal is currently a CAPTURE-ONLY surface. When
  // bundle gets a signature/proposal flow, the gate gets added then.
  const [showAssumptions,    setShowAssumptions]    = useState(false);
  const [assumptionsStory,   setAssumptionsStory]   = useState('');
  const [assumptionsSavedAt, setAssumptionsSavedAt] = useState(null);
  const assumptionsComplete = !!(assumptionsStory && assumptionsStory.trim().length > 0);
  const [saving,           setSaving]           = useState(false);
  const [saveMsg,          setSaveMsg]          = useState('');
  const [existingQuote,    setExistingQuote]    = useState(null);

  // Section collapse state
  const [itOpen,    setItOpen]    = useState(true);
  const [voiceOpen, setVoiceOpen] = useState(true);

  // IT state
  const [itInputs,    setItInputs]    = useState(DEF_IT);
  const [selectedPkg, setSelectedPkg] = useState(null);
  // v3.5.41: cost components loaded when selectedPkg changes
  const [costComponents, setCostComponents] = useState([]);
  const [selectedMkt, setSelectedMkt] = useState(null);

  // Voice state
  const [v, setV] = useState({ ...DEF_V, isManagedIT: true });

  // HubSpot

  const [hubDealId,   setHubDealId]   = useState('');
  const [hubDealName, setHubDealName] = useState('');
  const [hubDealUrl,  setHubDealUrl]  = useState('');

  const setIt  = useCallback((k, val) => setItInputs(p => ({ ...p, [k]: val })), []);
  const setVoice = useCallback((k, val) => setV(p => ({ ...p, [k]: val })), []);

  // Defaults
  useEffect(() => {
    if (!selectedPkg && packages.length) setSelectedPkg(packages[1] || packages[0]);
    if (!selectedMkt && marketTiers.length) setSelectedMkt(marketTiers.find(t => t.tier_key === 'mid_market') || marketTiers[0]);
  }, [packages, marketTiers]);

  // v3.5.41: load cost components when selected IT package changes
  useEffect(() => {
    if (!selectedPkg?.id) { setCostComponents([]); return; }
    loadPackageCostComponents(selectedPkg.id).then(rows => setCostComponents(rows));
  }, [selectedPkg?.id]);

  // v3.5.43: load real marketAnalysis for the rate sheet (mirrors QuotePage)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (existingQuote?.id) {
        try {
          const sheet = await getRateSheet(existingQuote.id);
          if (cancelled) return;
          if (sheet) {
            setMarketAnalysis({
              city:        sheet.market_city  || '',
              state:       sheet.market_state || '',
              zip:         sheet.market_zip   || null,
              market_tier: 'locked',
              pricing_multiplier: 1,
              rates:       sheet.accepted_rates || {},
              analysis_source: 'locked',
              analyzed_at: sheet.accepted_at || null,
            });
            return;
          }
        } catch { /* fall through */ }
      }
      if (clientZip && clientZip.length >= 5) {
        try {
          const res = await getOrAnalyzeMarket(clientZip, false, marketCity || undefined, marketState || undefined, true);
          if (cancelled) return;
          if (res?.analysis) {
            setMarketAnalysis(res.analysis);
            return;
          }
        } catch { /* fall through */ }
      }
      if (!cancelled) setMarketAnalysis(null);
    }
    load();
    return () => { cancelled = true; };
  }, [existingQuote?.id, clientZip, marketCity, marketState]);

  // ── Rep ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.from('profiles').select('id, full_name, email, commission_rate').order('full_name')
      .then(({ data }) => setTeamMembers(data || []));
  }, []);
  useEffect(() => {
    if (!repId && profile?.id && !id) setRepId(profile.id);
  }, [profile, id]);
  useEffect(() => {
    if (!repId || !teamMembers.length) return;
    const rep = teamMembers.find(m => m.id === repId);
    if (rep) setRepProfile(rep);
  }, [repId, teamMembers]);

  // Load existing
  useEffect(() => {
    if (!id || id === 'new' || configLoading) return;
    supabase.from('quotes').select('*').eq('id', id).single().then(({ data }) => {
      if (!data) return;
      setExistingQuote(data);
      setProposalName(data.inputs?.proposalName || '');
      setRecipientBiz(data.client_name || '');
      setRecipientContact(data.inputs?.recipientContact || '');
      setRecipientEmail(data.inputs?.recipientEmail || '');
      setRecipientAddress(data.inputs?.recipientAddress || '');
      setRecipientPhone(data.inputs?.recipientPhone || '');
      setRecipientMobilePhone(data.inputs?.recipientMobilePhone || '');
      setHubContactId(data.inputs?.hubspotContactId || '');
      setHubCompanyId(data.inputs?.hubspotCompanyId || '');
      setHubspotSnapshot(data.inputs?.hubspotSnapshot || null);
      setNotificationCc(data.inputs?.notificationCc || '');
      setAddendums(Array.isArray(data.inputs?.addendums) ? data.inputs.addendums : []);
      setManualDiscounts(Array.isArray(data.inputs?.manualDiscounts) ? data.inputs.manualDiscounts : []);
      setOneTimeDiscounts(Array.isArray(data.inputs?.oneTimeDiscounts) ? data.inputs.oneTimeDiscounts : []);
      setEffectiveDate(data.inputs?.effectiveDate || '');
      setClientZip(data.client_zip || '');
      setDealDescription(data.notes || '');
      setQuoteStatus(data.status || 'draft');
      setHubDealId(data.hubspot_deal_id || '');
      setHubDealUrl(data.hubspot_deal_url || '');
      setHubDealName(data.inputs?.hubspotDealName || '');
      if (data.rep_id) setRepId(data.rep_id);
      if (data.pricing_snapshot) {
        setPricingSnapshot(data.pricing_snapshot);
        setPriceLockDate(data.price_locked_at);
        if (data.pricing_snapshot.acceptedRates) setAcceptedRates(data.pricing_snapshot.acceptedRates);
      }
      if (data.spt_proposal_id) setSptProposalId(data.spt_proposal_id);
      if (data.inputs?.flexHours) setFlexHours(data.inputs.flexHours);
      if (data.inputs?.it) {
        // v3.5.58: auto-strip product IDs that no longer resolve to active
        // products — same generic cleanup as QuotePage. Cleans up legacy
        // flat-rate vCIO/Strategic Advisory line items now that ITSO is
        // derived from Industry Risk instead.
        const stripped = { ...DEF_IT, ...data.inputs.it };
        if (Array.isArray(stripped.selectedProducts) && products.length > 0) {
          const validIds = new Set(products.filter(p => p.active).map(p => p.id));
          stripped.selectedProducts = stripped.selectedProducts.filter(pid => validIds.has(pid));
        }
        setItInputs(stripped);
      }
      if (data.inputs?.voice) setV({ ...DEF_V, isManagedIT: true, ...data.inputs.voice });
      if (data.inputs?.package_name && packages.length) setSelectedPkg(packages.find(p => p.name === data.inputs.package_name));
      if (data.market_tier && marketTiers.length) setSelectedMkt(marketTiers.find(t => t.tier_key === data.market_tier));
      // v3.5.38: restore Market Intelligence override
      if (data.inputs?.aiMultiplier != null) {
        setAiMultiplier(data.inputs.aiMultiplier);
        setAiMultiplierTier(data.inputs.aiMultiplierTier || null);
      }
      if (data.inputs?.marketCity)  setMarketCity(data.inputs.marketCity);
      if (data.inputs?.marketState) setMarketState(data.inputs.marketState);
      // v3.5.44: restore onboarding incentive (column added in same release)
      if (data.onboarding_incentive?.mode) setObIncentive(data.onboarding_incentive);
      // v3.5.86.3: restore Customer Story for the at-a-glance status indicator.
      // Drives the ✓/⚠ on the Customer Story button. Same shape QuotePage and
      // MultiSiteQuotePage use.
      if (data.inputs?.assumptions) {
        setAssumptionsStory(data.inputs.assumptions.story || '');
        setAssumptionsSavedAt(data.inputs.assumptions.savedAt || null);
      }
    });
  }, [id, configLoading, packages, marketTiers]);

  // ── Handle conversion from IT or Voice quote ─────────────────────────────
  useEffect(() => {
    const from = location.state?.fromQuote;
    if (!from || id || configLoading || !packages.length || !marketTiers.length) return;

    // Populate shared client fields
    setProposalName(from.proposalName || '');
    setRecipientBiz(from.clientName   || '');
    setRecipientContact(from.recipientContact || '');
    setRecipientEmail(from.recipientEmail     || '');
    setRecipientAddress(from.recipientAddress || '');
    setRecipientPhone(from.recipientPhone || '');
    setRecipientMobilePhone(from.recipientMobilePhone || '');
    setHubContactId(from.hubspotContactId || '');
    setHubCompanyId(from.hubspotCompanyId || '');
    setHubspotSnapshot(from.hubspotSnapshot || null);
    setNotificationCc(from.notificationCc || '');
    setClientZip(from.clientZip || '');
    setDealDescription(from.notes || '');
    setHubDealId(from.hubDealId   || '');
    setHubDealUrl(from.hubDealUrl  || '');
    setHubDealName(from.hubDealName || '');

    // Restore market tier and package
    if (from.marketTier && marketTiers.length) {
      const tier = marketTiers.find(t => t.tier_key === from.marketTier);
      if (tier) setSelectedMkt(tier);
    }
    if (from.packageName && packages.length) {
      const pkg = packages.find(p => p.name === from.packageName);
      if (pkg) setSelectedPkg(pkg);
    }

    // Pre-populate IT section from an IT quote conversion
    if (from.type === 'it' && from.inputs) {
      setItInputs(prev => ({ ...prev, ...from.inputs }));
    }

    // Pre-populate Voice section from a Voice quote conversion
    if (from.type === 'voice' && from.voiceInputs) {
      setV(prev => ({ ...prev, isManagedIT: true, ...from.voiceInputs }));
    }
  }, [location.state, id, configLoading, packages, marketTiers]);

  // ── Unbundle handler ──────────────────────────────────────────────────────
  async function handleUnbundle(keepType) {
    if (!existingQuote) return;
    setUnbundling(true);
    try {
      if (keepType === 'it') {
        // Navigate to new IT quote pre-populated with bundle's IT data
        navigate('/quotes/new', { state: { fromBundle: {
          type: 'it',
          clientName:       existingQuote.client_name,
          clientZip:        existingQuote.client_zip,
          marketTier:       existingQuote.market_tier,
          packageName:      existingQuote.inputs?.package_name,
          proposalName:     existingQuote.inputs?.proposalName,
          recipientContact: existingQuote.inputs?.recipientContact,
          recipientEmail:   existingQuote.inputs?.recipientEmail,
          recipientAddress: existingQuote.inputs?.recipientAddress,
          recipientPhone: existingQuote.inputs?.recipientPhone,
          recipientMobilePhone: existingQuote.inputs?.recipientMobilePhone,
          notes:            existingQuote.notes,
          hubDealId:        existingQuote.hubspot_deal_id,
          hubDealUrl:       existingQuote.hubspot_deal_url,
          hubDealName:      existingQuote.inputs?.hubspotDealName,
          inputs:           existingQuote.inputs?.it || {},
          sourceQuoteId:    existingQuote.id,
          sourceQuoteNum:   existingQuote.quote_number,
        }}});
      } else if (keepType === 'voice') {
        // Navigate to new Voice quote pre-populated with bundle's Voice data
        navigate('/voice/new', { state: { fromBundle: {
          type: 'voice',
          clientName:       existingQuote.client_name,
          clientZip:        existingQuote.client_zip,
          marketTier:       existingQuote.market_tier,
          proposalName:     existingQuote.inputs?.proposalName,
          recipientContact: existingQuote.inputs?.recipientContact,
          recipientEmail:   existingQuote.inputs?.recipientEmail,
          recipientAddress: existingQuote.inputs?.recipientAddress,
          recipientPhone: existingQuote.inputs?.recipientPhone,
          recipientMobilePhone: existingQuote.inputs?.recipientMobilePhone,
          notes:            existingQuote.notes,
          hubDealId:        existingQuote.hubspot_deal_id,
          hubDealUrl:       existingQuote.hubspot_deal_url,
          hubDealName:      existingQuote.inputs?.hubspotDealName,
          voiceInputs:      existingQuote.inputs?.voice || {},
          sourceQuoteId:    existingQuote.id,
          sourceQuoteNum:   existingQuote.quote_number,
        }}});
      }
    } catch (err) {
      alert('Unbundle failed: ' + err.message);
    }
    setUnbundling(false);
    setShowUnbundle(false);
  }

  function handleZipChange(val) {
    setClientZip(val); setZipApplied(false);
    const r = val.length >= 3 ? lookupZip(val) : null;
    setZipResult(r);
  }
  function applyZip(r) {
    const res = r || zipResult;
    if (!res) return;
    const tier = marketTiers.find(t => t.tier_key === res.tier);
    if (tier) { setSelectedMkt(tier); setZipApplied(true); }
    // v3.5.38: also set marketCity/State so MarketRateCard receives them
    // (was a pre-existing gap on Bundle, fixed as part of MI wiring)
    if (res.city)  setMarketCity(res.city);
    if (res.state) setMarketState(res.state);
  }

  function toggleProduct(productId) {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    setItInputs(prev => {
      let selected = [...(prev.selectedProducts || [])];
      if (selected.includes(productId)) {
        selected = selected.filter(id => id !== productId);
      } else {
        if (product.exclusive_group) {
          const groupIds = exclusiveGroups[product.exclusive_group] || [];
          selected = selected.filter(id => !groupIds.includes(id));
        }
        selected.push(productId);
      }
      return { ...prev, selectedProducts: selected };
    });
  }
  function isSelected(pid) { return (itInputs.selectedProducts || []).includes(pid); }

  // HubSpot
  // Calculations
  const itResult = configLoading || !selectedPkg || !selectedMkt ? null
    : calcQuote({ inputs: itInputs, pkg: selectedPkg, marketTier: selectedMkt, products, settings, aiMultiplierOverride: aiMultiplier, costComponents });

  // Flex Time block — same pattern as QuotePage / MultiSiteQuotePage.
  // flexHours is null when no flex block is selected; both flexBlock and flexLaborCost
  // gracefully evaluate to null/0 in that case so the cost-model panel renders cleanly.
  // Rate chain: accepted (live) → snapshotted accepted → package default → market_tier (legacy) → 165 floor.
  const flexBlockRate  = acceptedRates?.remote_support
    ?? pricingSnapshot?.acceptedRates?.remote_support
    ?? selectedPkg?.rates?.remote_support
    ?? selectedMkt?.rates?.remote_support
    ?? 165;
  const flexBlock      = (itResult && flexHours)
    ? calcFlexBlock(flexHours, flexBlockRate, settings) : null;
  // v3.5.58: settings-driven burden helper (default $80, was $125 fallback).
  const burdenedRate   = getBurdenedRate(settings);
  const flexLaborCost  = flexBlock ? flexBlock.hours * burdenedRate : 0;
  const flexBlockMRR   = flexBlock?.blockPrice || 0;

  // IT base MRR = only the 6 package rates, no add-ons, no uplifts
  const itBaseMRR = itResult
    ? (itResult.wB + itResult.uB + itResult.sB + itResult.lB + itResult.tB + itResult.vB)
    : 0;

  // Bundle discount based on IT contract term
  const contractTerm = itInputs.contractTerm;
  const voiceForCalc = { ...v, isManagedIT: false }; // calc voice without its own bundle discount
  const voiceResult = configLoading ? null : calcVoice(voiceForCalc, settings, faxPackagesDB, hardwareCatalog, cxTiersDB);
  const bundle = voiceResult ? calcBundleDiscount(contractTerm, itBaseMRR, voiceResult.finalMRR, settings) : null;

  // v3.5.86.2.2: free phones derivation simplified. Previously this re-checked
  // `itBaseMRR >= 750` after asking calcBundleDiscount — redundant, since
  // calcBundleDiscount.freePhones already incorporates the threshold (now
  // settings-driven via bundle_free_phones_min_it_mrr). The duplicate check
  // would also have gone stale if an admin raised the threshold above 750
  // without a code change.
  const freePhones = !!bundle?.freePhones;
  // If free phones, override hardware NRC to 0
  // v3.5.92.2: rebuild uses calcVoice's new hardwareNrc field directly
  // (was a line-filter sum pre-deploy). Same numbers, simpler code. We
  // also zero the engine's hardwareNrc bucket on the rebuilt object so
  // that downstream consumers (including buildTotals below) see hardware=0
  // when freePhones is true.
  const voiceResultFinal = (() => {
    if (!voiceResult) return null;
    if (!freePhones) return { ...voiceResult, bundleVoiceDiscount: bundle?.voiceDiscount || 0 };
    // Rebuild lines with hardware NRC zeroed out (label tweak for the
    // proposal display — "$X" → "$0 (free — 36mo bundle)")
    const lines = voiceResult.lines.map(l =>
      l.section === 'hardware' && l.nrc > 0
        ? { ...l, nrc: 0, label: l.label.replace(/\$[\d,]+/, '$0') + ' (free — 36mo bundle)' }
        : l
    );
    const hwOriginal = Number(voiceResult.hardwareNrc) || 0;
    const adjustedNRC = (Number(voiceResult.nrc) || 0) - hwOriginal;
    return {
      ...voiceResult,
      lines,
      nrc: adjustedNRC,
      hardwareNrc: 0,             // v3.5.92.2 — keep the bucket field consistent with the rebuilt total
      bundleVoiceDiscount: bundle?.voiceDiscount || 0,
    };
  })();

  const voiceDiscountedMRR = voiceResultFinal ? voiceResultFinal.finalMRR - (bundle?.voiceDiscount || 0) : 0;
  // v3.5.99.7.23: manual discounts apply on top of the combined (post-bundle-
  // discount) MRR. combinedMRR becomes net, so combinedGM / combinedTCV and
  // every display site reflect it. The save passes combinedMRRPreDiscount to
  // buildTotals (which applies the discount itself) so it isn't double-counted.
  const combinedMRRPreDiscount = (itResult?.finalMRR || 0) + voiceDiscountedMRR;
  const manualDiscountResolved = applyManualDiscounts(combinedMRRPreDiscount, manualDiscounts);
  const combinedMRR = manualDiscountResolved.finalMRR;
  const combinedNRC = (itResult?.onboarding || 0) + (voiceResultFinal?.nrc || 0);
  const combinedCost = (itResult?.totalCost || 0) + (voiceResultFinal?.totalCost || 0);
  const combinedGM   = combinedMRR > 0 ? 1 - combinedCost / combinedMRR : 0;
  const combinedTCV  = combinedMRR * contractTerm + combinedNRC;

  // v3.5.92.2 §4a: onboarding-incentive picker base on Bundle.
  // Pre-deploy the picker used `combinedNRC` (IT onboarding + total voice
  // NRC including hardware + porting + activation). Per §4a, the picker
  // should discount ONLY Programming & Configuration labor. On Bundle
  // that's IT onboarding + Voice Programming&Config (excludes Voice
  // hardware, porting, activation — all flow through unchanged).
  // BEHAVIOR CHANGE: customer pays MORE on Bundle quotes with an active
  // incentive AND any voice porting/activation (the previously-discounted
  // pass-through fees now flow through at full value).
  const bundleOnboardingLabor = (itResult?.onboarding || 0) + (Number(voiceResultFinal?.onboardingNrc) || 0);

  // v3.7.8: one-time discount bases. Onboarding is the post-incentive composed
  // labor; hardware/activation/porting come from the (freePhones-rebuilt) Voice
  // result — the SAME fields buildBundleTotals reads, so the editor preview and
  // the persisted net agree.
  const oneTimeBases = {
    onboarding: (obIncentive?.effectiveFee != null ? Number(obIncentive.effectiveFee) : bundleOnboardingLabor),
    hardware:   Number(voiceResultFinal?.hardwareNrc)   || 0,
    activation: Number(voiceResultFinal?.activationNrc) || 0,
    porting:    Number(voiceResultFinal?.portingNrc)    || 0,
  };
  const oneTimeResolved = applyOneTimeDiscounts(oneTimeBases, oneTimeDiscounts);

  const gc = gmColor(combinedGM);
  const gb = gmBg(combinedGM);
  // v3.5.65: local mktColor/mktBg maps removed — use tierColor() and tierBg()
  // from marketRates.js, which know about both modern (premium/standard/
  // adjusted/secondary) and legacy (major_metro/mid_market/small_market/rural)
  // tier keys. See QuotePage for the full rationale.

  async function saveQuote() {
    // v3.5.99.4: block save if any addendum has unfilled required placeholders.
    // See QuotePage canonical block for rationale.
    const addendumErrors = validateAddendums(addendums, addendumTemplates);
    if (addendumErrors.length > 0) {
      const summary = addendumErrors.slice(0, 3).map(e => `\u2022 ${e.template_name}: ${e.placeholder_label || e.reason}`).join('\n');
      const more = addendumErrors.length > 3 ? `\n\u2026and ${addendumErrors.length - 3} more.` : '';
      alert(`Can't save \u2014 ${addendumErrors.length} addendum issue${addendumErrors.length === 1 ? '' : 's'}:\n\n${summary}${more}\n\nOpen Special Terms to fix or remove the addendum.`);
      return;
    }
    // v3.5.67: read-only gate — see QuotePage saveQuote for rationale.
    if (isReadOnly) {
      setSaveMsg('You don\'t have permission to save Bundle quotes. This quote is view-only.');
      return;
    }
    // v3.9.9.2: post-signature lock — a signed agreement freezes terms; persist ONLY notes.
    if (isQuoteLocked(existingQuote)) {
      setSaving(true); setSaveMsg('');
      try {
        if (existingQuote?.id) {
          await supabase.from('quotes').update({ notes: dealDescription }).eq('id', existingQuote.id);
          if (hubDealId && dealDescription) { try { await updateDealDescription(hubDealId, dealDescription); } catch { /* non-blocking */ } }
        }
        setSaveMsg('🔒 Locked — only internal notes were saved.');
      } catch (e) { setSaveMsg('✗ ' + (e?.message || 'Save failed')); }
      setSaving(false);
      return;
    }
    if (!recipientBiz.trim()) { setSaveMsg('Enter a client name first.'); return; }
    setSaving(true); setSaveMsg('');
    const allInputs = {
      // v3.9.9: spread existing inputs FIRST so signwellDocuments (sent SO / LOA /
      // IntlWaiver tracking) and any other non-form fields survive the save — a
      // save after a document was sent otherwise wiped the record (and the
      // webhook's auto-charge target). Matches FlexITQuotePage (v3.5.52).
      ...(existingQuote?.inputs || {}),
      proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc,
      hubspotDealName: hubDealName, package_name: selectedPkg?.name, flexHours: flexHours || null,
      it: itInputs, voice: v,
      // v3.5.38: persist Market Intelligence override + market location
      marketCity, marketState,
      aiMultiplier: aiMultiplier ?? null,
      aiMultiplierTier: aiMultiplierTier ?? null,
      // v3.5.99.7.23: persist manual discount lines
      manualDiscounts: manualDiscounts || [],
      // v3.7.8: persist one-time (non-recurring) discount lines
      oneTimeDiscounts: oneTimeDiscounts || [],
      // v3.5.99.7.24: persist back-dated agreement effective date
      effectiveDate: effectiveDate || null,
    };
    // v3.5.92.2: totals construction migrated to src/lib/quoteTotals.js
    // (Phase 1 item 2, sub-deploy 3 of 4). Identity persistence — same
    // object now goes to BOTH the upsert payload AND saveQuoteVersion
    // below, eliminating the §3.1 save-vs-snapshot mismatch (pre-deploy
    // payload.totals had 9 fields; the version-snapshot totals had 5;
    // Bundle-specific fields itMRR/voiceMRR/bundleDiscount/freePhones
    // were silently dropped from QuoteHistory).
    //
    // Key shape changes on save:
    //   • Bundle-specific fields renamed and moved into bundle: {...}
    //     bucket per §7 #7 — itMRR → bundleItFinalMRR, voiceMRR →
    //     bundleVoiceFinalMRR; bundleDiscount and freePhones retained.
    //   • NRC layer: per §4a, top-level `onboarding` is now the LABOR
    //     base only (itResult.onboarding + voice.onboardingNrc), NOT the
    //     total NRC. The total NRC = nrc field, which decomposes into
    //     onboarding + hardwareNRC + activationNRC + portingNRC.
    //   • ITSO + IT-engine internals (itSubtotal/compMult/discRate/
    //     discount) inherited from itResult — new persisted fields on
    //     Bundle (pre-deploy these were computed but never saved).
    //   • Incentive metadata (effectiveOnboarding etc.) finally lands
    //     in totals (pre-deploy only the top-level onboarding_incentive
    //     column had it; every consumer had to redo the math).
    const totals = buildTotals('bundle', {
      itResult,
      voiceResult: voiceResultFinal,
      itBaseMRR,
      bundleVoiceDiscount: bundle?.voiceDiscount || 0,
      // v3.5.99.7.23: pass the PRE-discount combined MRR — buildBundleTotals
      // applies the manual discount itself, so passing the already-netted
      // combinedMRR here would double-count it.
      combinedMRR: combinedMRRPreDiscount,
      combinedCost,
      freePhones,
    }, {
      obIncentive,
      flexBlock,
      contractTerm,
      settings,
      manualDiscounts,
      oneTimeDiscounts,
    });
    const payload = {
      client_name: recipientBiz, client_zip: clientZip,
      market_tier: selectedMkt?.tier_key,
      package_name: selectedPkg?.name || 'IT',
      // v3.5.91.0: dual-write quote_type. Canonical key 'bundle' matches
      // src/lib/quotePermissions.js QUOTE_TYPES + profiles.allowed_quote_types
      // + RLS. v3.5.91.2: this column is now the SOLE type discriminator
      // (package_name no longer carries the 'Bundle — ' prefix or
      // ' + Voice' suffix).
      quote_type: 'bundle',
      status: quoteStatus, notes: dealDescription, inputs: allInputs,
      line_items: [...(itResult?.lineItems || []), ...(voiceResultFinal?.lines || [])],
      totals,
      hubspot_deal_id: hubDealId || null, hubspot_deal_url: hubDealUrl || null,
      // v3.5.44: persist onboarding incentive.
      // v3.5.92.2 §4a: fullFee changed from combinedNRC (full all-in NRC)
      // to bundleOnboardingLabor (Programming labor only) — that's the
      // amount the picker actually discounts against, so it's what
      // fullFee should reflect for the proposal renderer's display.
      onboarding_incentive: obIncentive?.mode ? {
        mode:           obIncentive.mode,
        effectiveFee:   obIncentive.effectiveFee,
        monthlyAdd:     obIncentive.monthlyAdd   || 0,
        splitMonths:    obIncentive.splitMonths  || 0,
        discountPct:    obIncentive.discountPct  || 0,
        discountAmount: obIncentive.discountAmount || 0,
        fullFee:        bundleOnboardingLabor || null,
      } : null,
      rep_id:     repId || profile?.id || null,
      ...(quoteStatus === 'approved' && !pricingSnapshot ? { pricing_snapshot: { lockedAt: new Date().toISOString(), acceptedRates: acceptedRates || null }, price_locked_at: new Date().toISOString(), price_locked_by: profile?.id } : {}),
      ...(pricingSnapshot ? { pricing_snapshot: pricingSnapshot, price_locked_at: priceLockDate } : {}),
      updated_by: profile?.id,
    };
    if (!existingQuote) payload.created_by = profile?.id;

    const { data, error } = existingQuote
      ? await supabase.from('quotes').update(payload).eq('id', existingQuote.id).select().single()
      : await supabase.from('quotes').insert(payload).select().single();

    if (error) { setSaveMsg('Error: ' + error.message); setSaving(false); return; }

    if (hubDealId && dealDescription) {
      try { await updateDealDescription(hubDealId, dealDescription); } catch {}
    }

    await logActivity({ action: existingQuote ? 'UPDATE' : 'CREATE', entityType: 'quote', entityId: data.id, entityName: recipientBiz,
      changes: { type: 'bundle', mrr: combinedMRR, it_mrr: itResult?.finalMRR, voice_mrr: voiceDiscountedMRR } });
    // Write quote URL back to HubSpot deal if a field is configured
    if (hubDealId && data?.id) {
      try {
        const { data: fieldData } = await supabase.from('pricing_settings').select('value').eq('key','hubspot_quote_url_field').single();
        const fieldKey = fieldData?.value;
        if (fieldKey) {
          const quoteUrl = `${window.location.origin}/bundle/${data.id}`;
          await writeQuoteUrlToDeal(hubDealId, quoteUrl, fieldKey);
        }
      } catch (e) { console.warn('HubSpot quote URL write failed:', e.message); }
    }

    // v3.5.92.2: identity persistence — snapshot uses the SAME `totals`
    // object as the upsert payload above. Pre-deploy this site passed a
    // 5-field subset that dropped Bundle-specific fields from QuoteHistory
    // (§3.1 bug). Fixed by construction.
    await saveQuoteVersion({
      quoteId: data.id,
      quoteData: { client_name: recipientBiz, client_zip: clientZip, market_tier: selectedMkt?.tier_key, package_name: selectedPkg?.name || 'IT', status: quoteStatus },
      inputs: { proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc, addendums, package_name: selectedPkg?.name, it: itInputs, voice: v },
      totals,
      lineItems: [...(itResult?.lineItems || []), ...(voiceResultFinal?.lines || [])],
      profile,
    });

    setSaveMsg(`Saved as ${data.quote_number}${hubDealId && dealDescription ? ' · HubSpot updated' : ''}`);
    // v3.5.99.7.4: gap-fill push to HubSpot. Fire-and-forget after save
    // success — pushes only fields where HubSpot was empty when connectDeal
    // pulled (per hubspotSnapshot). Errors logged but don't block UI.
    // The manual full-overwrite push button comes in v3.5.99.7.5.
    if ((hubContactId || hubCompanyId) && hubspotSnapshot) {
      pushHubspotGapFill({
        contactId: hubContactId, companyId: hubCompanyId, snapshot: hubspotSnapshot,
        recipientContact, recipientEmail, recipientPhone, recipientMobilePhone, recipientAddress,
      }).then(r => {
        if (r.updatedSnapshot) setHubspotSnapshot(r.updatedSnapshot);
        if (r.errors?.length) console.warn('HubSpot gap-fill errors:', r.errors);
      }).catch(e => console.warn('HubSpot gap-fill failed:', e));
    }
    setSaving(false);
    if (!existingQuote) navigate(`/bundle/${data.id}`, { replace: true });
  }

  // v3.5.83.2.1: derived flag for the Lock-Rates-First UI guard. Mirrors
  // QuotePage.js's pattern — non-null object with at least one key matches
  // the v3.5.82.4.1 backend precondition shape exactly.
  const ratesLocked = !!(
    acceptedRates &&
    typeof acceptedRates === 'object' &&
    Object.keys(acceptedRates).length > 0
  );

  // v3.5.83.2.1: scroll helper for "Lock Rates Now" + banner button. Targets
  // the <div id="oos-rate-card"> wrapper around <MarketRateCard>.
  const V3_BUNDLE = (() => {
    try {
      if (typeof window !== 'undefined') {
        const q = new URLSearchParams(window.location.search);
        if (q.get('v3') === '1') return true;
        if (q.get('v3') === '0') return false;
        const stored = window.localStorage.getItem('ferrumiq_v3_bundle');
        if (stored === '1') return true;
        if (stored === '0') return false;                     // persistent opt-out
        return true;   // v3.8.6 — Bundle workspace is now the DEFAULT (Shaun 2026-08-04).
      }                //          Classic layout reachable via ?v3=0 / stored '0'.
    } catch (e) { /* disabled storage */ }
    return true;
  })();

  const scrollToOosRateCard = () => {
    const el = document.getElementById('oos-rate-card');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─── v3.8.5 Unified-UX workspace (flag ferrumiq_v3_bundle, DEFAULT OFF) ───
  // Same pattern as Managed IT + Voice: legacy two-pane return below is
  // untouched; the v3 branch reuses every handler + child component + the
  // verbatim <Sec> input blocks. combinedMRRPreDiscount → buildTotals and
  // combinedMRR (net) → display stay exactly as the legacy computes them.
  if (V3_BUNDLE) {
    const activeSection = bundleFocusedSection || (existingQuote?.id ? 'bundle' : 'client');
    const statusVariant = ({ won: 'green', approved: 'green', sent: 'blue', lost: 'red', expired: 'red', in_review: 'amber', draft: 'amber' })[quoteStatus] || 'gray';
    const t2 = (t) => (<h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>{t}</h2>);
    const s2 = (t) => (<p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6, marginBottom: 4 }}>{t}</p>);

    const v3Sections = [
      { id: 'client',            label: 'Client',           value: recipientBiz || 'Not set', status: recipientBiz ? 'ok' : 'empty' },
      { id: 'it-basis',          label: 'IT — basis',       value: `${selectedMkt?.name || '—'} · ${selectedPkg?.name || '—'} · ${contractTerm} mo`, status: selectedPkg ? 'ok' : 'empty' },
      { id: 'it-infrastructure', label: 'IT — infrastructure', value: `${itInputs.users || 0} users · ${itInputs.workstations || 0} wks · ${itInputs.servers || 0} srv`, status: 'ok' },
      { id: 'it-products',       label: 'IT — products',    value: `${(itInputs.selectedProducts || []).length} selected`, status: 'ok' },
      { id: 'v-mode',            label: 'Voice — mode',     value: v.quoteType === 'hosted' ? 'Hosted' : v.quoteType === 'hybrid' ? 'Hybrid' : 'SIP', status: 'ok' },
      { id: 'v-seats',           label: 'Voice — seats',    value: v.quoteType === 'hosted' ? `${v.seats || 0} seats` : 'Hosted only', status: 'ok' },
      { id: 'v-numbers',         label: 'Voice — numbers',  value: `${v.localDIDs || 0} local · ${v.tollFreeNumbers || 0} TF`, status: 'ok' },
      { id: 'v-hardware',        label: 'Voice — hardware', value: ({ none: 'None', lease: 'Lease', purchase: 'Purchase' })[v.hardwareType] || 'None', status: 'ok' },
      { id: 'bundle',            label: 'Bundle discount',  value: bundle?.qualifies ? `${Math.round((bundle.rate || 0) * 100)}% · ${fmt$0(bundle.voiceDiscount || 0)} off` : 'Not qualified', status: 'ok' },
      { id: 'onboarding',        label: 'Onboarding',       value: bundleOnboardingLabor > 0 ? `${fmt$0(obIncentive?.effectiveFee ?? bundleOnboardingLabor)}` : '—', status: 'ok' },
      { id: 'pricing',           label: 'Pricing',          value: `${fmt$0(combinedMRR)}/mo`, status: 'ok' },
      { id: 'documents',         label: 'Documents & send', value: existingQuote?.id ? 'Rates · SO · pay' : 'Save first', status: 'ok' },
      { id: 'terms',             label: 'Special terms',    value: `${addendums.length} addendum${addendums.length === 1 ? '' : 's'}`, status: addendums.length ? 'ok' : 'empty' },
      { id: 'engagement',        label: 'Engagement',       value: existingQuote?.id ? 'View activity' : 'Not sent', status: 'ok' },
      { id: 'history',           label: 'History',          value: existingQuote?.id ? 'Versions' : '—', status: existingQuote?.id ? 'ok' : 'empty' },
    ];

    const v3Header = (
      <QuoteHeader id={existingQuote?.quote_number || 'New bundle quote'} title={proposalName || recipientBiz || 'Bundle quote'} status={quoteStatus} statusVariant={statusVariant}
        extraPills={[{ variant: 'blue', label: 'Managed IT + Voice' }, ...(hubDealId ? [{ variant: 'green', label: 'HubSpot linked' }] : [])]} savedLabel={saving ? 'Saving…' : (saveMsg || undefined)}>
        <V3Button variant="secondary" size="sm" onClick={() => setSoPreviewModalOpen(true)} disabled={!existingQuote?.id}>Preview SO</V3Button>
        <V3Button variant="dark" size="sm" onClick={saveQuote} disabled={saving || isReadOnly}>{saving ? 'Saving…' : 'Save'}</V3Button>
      </QuoteHeader>
    );

    const v3Rail = (
      <>
        <LiveTotalCard headline={fmt$0(combinedMRR)} rows={[
          { label: 'IT recurring',   value: fmt$0(itResult?.finalMRR || 0) },
          { label: 'Voice recurring',value: fmt$0(voiceDiscountedMRR) },
          ...(bundle?.voiceDiscount > 0 ? [{ label: 'Bundle saving', value: `−${fmt$0(bundle.voiceDiscount)}`, color: v3.color.moneyPositive }] : []),
          { label: 'One-time',       value: fmt$0(combinedNRC) },
          { label: 'Contract value', value: fmt$0(combinedTCV) },
          { label: 'Gross margin',   value: fmtPct(combinedGM), color: v3.color.moneyPositive },
        ]} />
        <RailCard title="Cost model — monthly">
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}><span style={{ color: v3.color.muted }}>Combined cost</span><span style={{ fontFamily: v3.font.mono, fontWeight: 600 }}>{fmt$0(combinedCost)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 6px', background: gmBg(combinedGM), borderRadius: 4, marginTop: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: gmColor(combinedGM) }}>Implied Gross Margin</span>
            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: v3.font.mono, color: gmColor(combinedGM) }}>{fmtPct(combinedGM)}</span>
          </div>
          {combinedGM < 0.40 && <div style={{ marginTop: 4, fontSize: 11, color: v3.color.status.amberText }}>⚠ Below 40% — review scope.</div>}
        </RailCard>
        {bundle?.qualifies && (
          <RailCard title="Bundle discount">
            <div style={{ fontSize: 13, color: v3.color.body }}>{Math.round((bundle.rate || 0) * 100)}% off Voice MRR · <strong>{fmt$0(bundle.voiceDiscount || 0)}/mo</strong></div>
            {freePhones && <div style={{ marginTop: 4, fontSize: 12, color: v3.color.status.greenText }}>✓ Phones included free</div>}
          </RailCard>
        )}
      </>
    );

    const renderBundleFocus = () => {
      switch (activeSection) {
        case 'client':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Client')}{s2('Recipient and linked HubSpot deal.')}
            <HubSpotConnect dealId={hubDealId} dealUrl={hubDealUrl} dealName={hubDealName} description={dealDescription} onDescriptionChange={setDealDescription} quoteNumber={existingQuote?.quote_number} mrr={bundle?.finalMRR} contractValue={bundle?.contractValue} packageName={selectedPkg?.name} contractTerm={contractTerm} existingQuoteId={existingQuote?.id} clientName={recipientBiz}
              onConnect={full => { setHubDealId(full.dealId); setHubDealUrl(full.dealUrl); setHubDealName(full.deal.dealname); if (full.company) { if (full.company.name) setRecipientBiz(full.company.name); const addr = [full.company.address, full.company.city, full.company.state, full.company.zip].filter(Boolean).join(', '); if (addr) setRecipientAddress(addr); if (full.company.zip) { setClientZip(full.company.zip); const zr = lookupZip(full.company.zip); setZipResult(zr); if (zr) applyZip(zr); } } else { const x = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim(); if (x) setRecipientBiz(x); } if (full.contact) { const n=[full.contact.firstname,full.contact.lastname].filter(Boolean).join(' '); if(n) setRecipientContact(n); if(full.contact.email) setRecipientEmail(full.contact.email); if(full.contact.phone) setRecipientPhone(full.contact.phone); if(full.contact.mobilephone) setRecipientMobilePhone(full.contact.mobilephone); } if (full.contactId) setHubContactId(full.contactId); if (full.companyId) setHubCompanyId(full.companyId); setHubspotSnapshot(captureHubspotSnapshot(full)); if (!proposalName && full.deal.dealname) setProposalName(`FerrumIT Bundle — ${full.company?.name||full.deal.dealname}`); }}
              onDisconnect={() => { setHubDealId(''); setHubDealUrl(''); setHubDealName(''); }} />
            <SPTConnect proposalId={sptProposalId} quoteId={existingQuote?.id} clientName={recipientBiz} quoteNumber={existingQuote?.quote_number} settings={settings} onConnect={(pid) => setSptProposalId(pid)} onDisconnect={() => setSptProposalId(null)} />
            <SubCard eyebrow="Proposal Details">
              <Fld lbl="Assigned Sales Rep"><RepSelector repId={repId} setRepId={setRepId} teamMembers={teamMembers} /></Fld>
              <Fld lbl="Proposal Name"><TI value={proposalName} onChange={setProposalName} placeholder="FerrumIT Bundle — Acme Corp"/></Fld>
              <Fld lbl="Client Business Name"><TI value={recipientBiz} onChange={setRecipientBiz} placeholder="Acme Corp"/></Fld>
              <Grid2><Fld lbl="Contact Name"><TI value={recipientContact} onChange={setRecipientContact} placeholder="Jane Smith"/></Fld><Fld lbl="Contact Email"><TI value={recipientEmail} onChange={setRecipientEmail} placeholder="jane@acme.com"/></Fld></Grid2>
              <Fld lbl="Business Address"><TI value={recipientAddress} onChange={setRecipientAddress} placeholder="123 Main St, Chicago, IL 60601"/></Fld>
              <Grid2><Fld lbl="Office Phone"><TI value={recipientPhone} onChange={setRecipientPhone} placeholder="(555) 123-4567"/></Fld><Fld lbl="Mobile Phone"><TI value={recipientMobilePhone} onChange={setRecipientMobilePhone} placeholder="(555) 765-4321"/></Fld></Grid2>
              {(hubContactId || hubCompanyId) && (<div style={{ marginTop: 8 }}><V3Button variant="secondary" size="sm" onClick={pushAllToHubspot} disabled={hubPushing}>{hubPushing ? '⏳ Pushing…' : '↑ Push to HubSpot'}</V3Button>{hubPushMsg && <span style={{ fontSize: 11, color: v3.color.muted, marginLeft: 8 }}>{hubPushMsg}</span>}</div>)}
              <div style={{ marginTop: 10 }}><Fld lbl="CC on Approval (comma-separated)"><TI value={notificationCc} onChange={setNotificationCc} placeholder="alice@ferrumit.com, bob@ferrumit.com"/></Fld></div>
              <Fld lbl="Zip Code"><div style={{ display:'flex', gap:5, alignItems:'center' }}><input value={clientZip} onChange={e=>handleZipChange(e.target.value)} placeholder="60601" style={{ flex:1, padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', outline:'none' }}/>{zipResult && !zipApplied && (<button onClick={()=>applyZip()} style={{ padding:'3px 7px', background:tierColor(zipResult.tier), color:'white', border:'none', borderRadius:3, fontSize:9, fontWeight:700, cursor:'pointer' }}>Apply →</button>)}</div>{zipResult && <div style={{ fontSize:9, color:'#6b7280', marginTop:2 }}>{zipResult.name}{zipApplied ? ' ✓' : ''}</div>}</Fld>
            </SubCard></div>);
        case 'it-basis':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Managed IT — basis')}{s2('Market, package, and the shared contract term (drives the bundle discount).')}
            <SubCard eyebrow="Market Tier">
              {showMktRecommend && pendingMultiplier && (<div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:5, padding:'7px 9px', marginBottom:6 }}><div style={{ fontSize:10, fontWeight:700, color:'#1e40af', marginBottom:3 }}>📊 AI Market Recommendation</div><div style={{ display:'flex', gap:4 }}><button onClick={() => { setAiMultiplier(pendingMultiplier.multiplier); setAiMultiplierTier(pendingMultiplier.tier); setShowMktRecommend(false); }} style={{ padding:'3px 10px', background:'#1e40af', color:'white', border:'none', borderRadius:3, fontSize:9, fontWeight:700, cursor:'pointer' }}>Apply AI Rate</button><button onClick={() => setShowMktRecommend(false)} style={{ padding:'3px 7px', background:'white', border:'1px solid #d1d5db', borderRadius:3, fontSize:9, color:'#6b7280', cursor:'pointer' }}>Keep Manual</button></div></div>)}
              <div style={{ marginBottom: 6 }}><MarketStatusBadge aiMultiplier={aiMultiplier} aiMultiplierTier={aiMultiplierTier} selectedMkt={selectedMkt} marketCity={marketCity} marketState={marketState} locked={!!pricingSnapshot} compact={true} onRunAnalysis={aiMultiplier != null ? (() => { setAiMultiplier(null); setAiMultiplierTier(null); }) : undefined} /></div>
              {marketTiers.map(t => (<div key={t.id} onClick={() => { setSelectedMkt(t); setAiMultiplier(null); setAiMultiplierTier(null); }} style={{ padding:'4px 7px', borderRadius:4, cursor:'pointer', marginBottom:2, border:`${selectedMkt?.id===t.id?'2':'1'}px solid ${selectedMkt?.id===t.id?tierColor(t.tier_key):'#e5e7eb'}`, background:selectedMkt?.id===t.id?tierBg(t.tier_key):'white', display:'flex', justifyContent:'space-between', alignItems:'center', opacity: aiMultiplier != null ? 0.55 : 1 }}><span style={{ fontSize:9, fontWeight:700, color:tierColor(t.tier_key) }}>{staticTierToMILabel(t.tier_key)} <span style={{ fontWeight:400, color:'#9ca3af' }}>({t.name})</span></span></div>))}
            </SubCard>
            <SubCard eyebrow="Package">
              {packages.map(p => (<div key={p.id} onClick={() => setSelectedPkg(p)} style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', marginBottom:2, border:`${selectedPkg?.id===p.id?'2':'1'}px solid ${selectedPkg?.id===p.id?'#2563eb':'#e5e7eb'}`, background:selectedPkg?.id===p.id?'#eff6ff':'white' }}><div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ fontSize:9, fontWeight:700, color:selectedPkg?.id===p.id?'#1e40af':'#374151' }}>{p.name}</span><span style={{ fontSize:8, fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${p.ws_rate}/WS · ${p.user_rate}/US</span></div></div>))}
              <Fld lbl="Quote Status" s={{ marginTop:8 }}><SI v={quoteStatus} s={setQuoteStatus} opts={[['draft','Draft'],['in_review','In Review'],['approved','Approved'],['sent','Sent'],['won','Won'],['lost','Lost'],['expired','Expired']]}/></Fld>
            </SubCard>
            <SubCard eyebrow="Contract Term"><div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4 }}>{getBundleVoiceDiscOptions(settings).map(([term,lbl,sub])=>(<div key={term} onClick={()=>setIt('contractTerm',term)} style={{ padding:'6px 5px', borderRadius:4, cursor:'pointer', textAlign:'center', border:`${itInputs.contractTerm===term?'2':'1'}px solid ${itInputs.contractTerm===term?'#7c3aed':'#e5e7eb'}`, background:itInputs.contractTerm===term?'#faf5ff':'white' }}><div style={{ fontSize:10, fontWeight:700, color:itInputs.contractTerm===term?'#6d28d9':'#374151' }}>{lbl}</div><div style={{ fontSize:8, color:'#9ca3af', marginTop:1 }}>{sub}</div></div>))}</div></SubCard>
          </div>);
        case 'it-infrastructure':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Managed IT — infrastructure')}{s2('Counts and risk profile driving IT pricing.')}
            <SubCard eyebrow="People & Devices"><Grid2><Fld lbl="Users"><NI v={itInputs.users} s={val=>setIt('users',val)}/></Fld><Fld lbl="Shared Mailboxes"><NI v={itInputs.sharedMailboxes} s={val=>setIt('sharedMailboxes',val)}/></Fld><Fld lbl="Workstations"><NI v={itInputs.workstations} s={val=>setIt('workstations',val)}/></Fld><Fld lbl="Total Devices"><NI v={itInputs.endpoints} s={val=>setIt('endpoints',val)}/></Fld><Fld lbl="Mobile Devices"><NI v={itInputs.mobileDevices||0} s={val=>setIt('mobileDevices',val)}/></Fld></Grid2></SubCard>
            <SubCard eyebrow="Infrastructure"><Grid2><Fld lbl="Servers"><NI v={itInputs.servers} s={val=>setIt('servers',val)}/></Fld><Fld lbl="Locations"><NI v={itInputs.locations} s={val=>setIt('locations',val)}/></Fld><Fld lbl="Cloud Tenants"><NI v={itInputs.cloudTenants} s={val=>setIt('cloudTenants',val)}/></Fld><Fld lbl="Domains"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ flex: 1 }}><NI v={itInputs.domains} s={val=>setIt('domains',val)}/></div><DomainsInfo /></div></Fld><Fld lbl="Vendors"><NI v={itInputs.vendors} s={val=>setIt('vendors',val)}/></Fld></Grid2></SubCard>
            <SubCard eyebrow="Risk & Compliance"><Grid2><Fld lbl="Threat Profile" sub="oversight tier"><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ flex: 1 }}><SI v={itInputs.industryRisk} s={val=>setIt('industryRisk',val)} opts={[['none','None'],['low','Standard'],['medium','Elevated'],['high','Critical']]}/></div><ThreatProfileInfo /></div></Fld><Fld lbl="Compliance" sub="adds program product"><SI v={itInputs.compliance} s={val=>setIt('compliance',val)} opts={[['none','None'],['hipaa','HIPAA'],['soc2','SOC 2'],['pci','PCI DSS'],['cmmc','CMMC']]}/></Fld><Fld lbl="Complexity"><SI v={itInputs.complexity} s={val=>setIt('complexity',val)} opts={[['low','Low'],['medium','Medium'],['high','High']]}/></Fld><Fld lbl="Coverage"><SI v={itInputs.requestedCoverage} s={val=>setIt('requestedCoverage',val)} opts={[['business_hours','8×5'],['24x5','24×5'],['24x7','24×7']]}/></Fld></Grid2></SubCard>
          </div>);
        case 'it-products':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Managed IT — products')}{s2('Add-on services layered on the package.')}
            {Object.entries(productsByCategory).map(([cat, catProducts]) => { const visibleProducts = catProducts.filter(p => !p.internal_only); if (visibleProducts.length === 0) return null; return (
              <SubCard key={cat} eyebrow={cat}>{visibleProducts.map(p => { const sel = isSelected(p.id); const qty = parseInt(itInputs.manualQuantities?.[p.id] || 0); return (
                <div key={p.id} onClick={() => toggleProduct(p.id)} style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 6px', borderRadius:3, cursor:'pointer', marginBottom:2, border:`1px solid ${sel?'#93c5fd':'#e5e7eb'}`, background:sel?'#eff6ff':'white' }}>
                  <div style={{ width:20, height:12, borderRadius:6, flexShrink:0, background:sel?'#2563eb':'#d1d5db', position:'relative' }}><div style={{ position:'absolute', top:2, left:sel?10:2, width:8, height:8, borderRadius:'50%', background:'white', transition:'left .1s' }}/></div>
                  <span style={{ fontSize:9, fontWeight:600, flex:1, color:sel?'#1e40af':'#374151' }}>{p.name}</span>
                  {sel && p.qty_driver === 'manual' && (<div onClick={e=>e.stopPropagation()} style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:0 }}><input type="number" min="0" value={qty || ''} placeholder="qty" onChange={e=>{ const next = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0); setItInputs(prev => ({ ...prev, manualQuantities: { ...(prev.manualQuantities || {}), [p.id]: next } })); }} style={{ width:48, padding:'1px 4px', border:`1px solid ${qty > 0 ? '#93c5fd' : '#fbbf24'}`, borderRadius:3, fontSize:9, fontFamily:'DM Mono, monospace', textAlign:'center', outline:'none', background: qty > 0 ? 'white' : '#fffbeb' }}/>{qty === 0 && <span style={{ fontSize:7, color:'#92400e', fontWeight:600, marginTop:1 }}>set qty</span>}</div>)}
                  <span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${p.sell_price}/{p.qty_driver === 'manual' ? 'lic' : p.qty_driver}</span>
                </div>); })}</SubCard>); })}
          </div>);
        case 'v-mode':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Hosted Voice — mode')}{s2('Deployment model and trunking.')}
            <SubCard eyebrow="Voice Type"><div style={{ display:'grid', gap:3 }}>{[['hosted','Hosted Voice — Per Seat'],['hybrid','Hybrid Hosting — BYOPBX'],['sip','SIP Trunking Only']].map(([key,lbl])=>(<div key={key} onClick={()=>setVoice('quoteType',key)} style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', border:`${v.quoteType===key?'2':'1'}px solid ${v.quoteType===key?'#7c3aed':'#e5e7eb'}`, background:v.quoteType===key?'#faf5ff':'white' }}><span style={{ fontSize:9, fontWeight:700, color:v.quoteType===key?'#6d28d9':'#374151' }}>{lbl}</span></div>))}</div></SubCard>
            {(v.quoteType === 'hybrid' || v.quoteType === 'sip') && (
              <SubCard eyebrow="SIP Channels"><Fld lbl="Concurrent Call Paths" sub="$19.95/path/mo"><NI v={v.sipChannels} s={val=>setVoice('sipChannels',val)}/></Fld>
                {v.quoteType === 'hybrid' && (<><Fld lbl="3CX License Tier" style={{ marginTop:4 }}><select value={v.cxTierId} onChange={e=>setVoice('cxTierId',e.target.value)} style={{ width:'100%', padding:'4px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}>{(() => { const tiers = (Array.isArray(cxTiersDB) && cxTiersDB.length > 0) ? cxTiersDB : CX_TIERS; const fmtCost = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); return (<><optgroup label="Pro">{tiers.filter(t => t.type === 'pro').map(t => (<option key={t.id} value={t.id}>Pro {t.paths} paths — ${fmtCost(t.annual_cost)}/yr</option>))}</optgroup><optgroup label="Enterprise">{tiers.filter(t => t.type === 'enterprise').map(t => (<option key={t.id} value={t.id}>Ent. {t.paths} paths — ${fmtCost(t.annual_cost)}/yr</option>))}</optgroup></>); })()}</select></Fld><Tog on={v.clientPaysMonthly} set={val=>setVoice('clientPaysMonthly',val)} lbl="Amortize license monthly"/><Tog on={v.largerInstance} set={val=>setVoice('largerInstance',val)} lbl="Enhanced VM instance"/></>)}
              </SubCard>)}
          </div>);
        case 'v-seats':
          if (v.quoteType !== 'hosted') return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Hosted Voice — seats')}{s2('Per-seat config applies to Hosted Voice. Switch the Voice mode to Hosted.')}</div>);
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Hosted Voice — seats')}{s2('Billable seats, license, and non-billable devices.')}
            <SubCard eyebrow="Seats"><Grid2><Fld lbl="Billable Seats"><NI v={v.seats} s={val=>setVoice('seats',val)}/></Fld><Fld lbl="Sell Price/Seat ($)"><NI v={v.seatPrice} s={val=>setVoice('seatPrice',val)}/></Fld></Grid2>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4, marginTop:4 }}>{[['pro','3CX Pro'],['enterprise','3CX Enterprise']].map(([key,lbl])=>(<div key={key} onClick={()=>setVoice('licenseType',key)} style={{ padding:'5px 6px', borderRadius:3, cursor:'pointer', textAlign:'center', border:`${v.licenseType===key?'2':'1'}px solid ${v.licenseType===key?'#2563eb':'#e5e7eb'}`, background:v.licenseType===key?'#eff6ff':'white', gridColumn: key==='pro'?'1/2':'2/4' }}><div style={{ fontSize:9, fontWeight:700, color:v.licenseType===key?'#1e40af':'#374151' }}>{lbl}</div></div>))}</div>
              <SubCard eyebrow="Non-Billable Devices"><Grid2><Fld lbl="Common Area"><NI v={v.commonAreaPhones} s={val=>setVoice('commonAreaPhones',val)}/></Fld><Fld lbl="Voicemail Only"><NI v={v.voicemailOnly} s={val=>setVoice('voicemailOnly',val)}/></Fld><Fld lbl="Door Phones"><NI v={v.doorPhones} s={val=>setVoice('doorPhones',val)}/></Fld><Fld lbl="Paging"><NI v={v.pagingDevices} s={val=>setVoice('pagingDevices',val)}/></Fld></Grid2></SubCard>
            </SubCard></div>);
        case 'v-numbers':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Hosted Voice — numbers & add-ons')}{s2('DIDs, fax, and voice add-ons.')}
            <SubCard eyebrow="Numbers & DIDs"><Grid2><Fld lbl="Local DIDs" sub="$1.50"><NI v={v.localDIDs} s={val=>setVoice('localDIDs',val)}/></Fld><Fld lbl="SMS DIDs" sub="+$1.25"><NI v={v.smsDIDs} s={val=>setVoice('smsDIDs',val)}/></Fld><Fld lbl="Toll-Free" sub="$5.00"><NI v={v.tollFreeNumbers} s={val=>setVoice('tollFreeNumbers',val)}/></Fld><Fld lbl="E911 DIDs" sub="$2.50"><NI v={v.e911DIDs} s={val=>setVoice('e911DIDs',val)}/></Fld><Fld lbl="Port Numbers" sub="$25 NRC"><NI v={v.portingNumbers} s={val=>setVoice('portingNumbers',val)}/></Fld></Grid2></SubCard>
            <SubCard eyebrow="Fax & Add-ons"><Fld lbl="Fax Package"><select value={v.faxType} onChange={e=>setVoice('faxType',e.target.value)} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}><option value="none">No Fax</option>{Object.entries(getFaxPackages(faxPackagesDB)).map(([key, pkg]) => (<option key={key} value={key}>{pkg.label} — ${pkg.price.toFixed(2)}/mo</option>))}</select></Fld><Tog on={v.callRecording} set={val=>setVoice('callRecording',val)} lbl="Call Recording" sub="$15/mo"/><Tog on={v.smsEnabled} set={val=>setVoice('smsEnabled',val)} lbl="SMS/MMS" sub="10DLC + metered"/></SubCard>
          </div>);
        case 'v-hardware':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Hosted Voice — hardware')}{s2('Phones — lease or purchase — and programming.')}
            <SubCard eyebrow="Hardware"><Fld lbl="Hardware Type"><select value={v.hardwareType} onChange={e=>setVoice('hardwareType',e.target.value)} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}><option value="none">No Hardware</option><option value="lease">Evergreen Lease</option><option value="purchase">Outright Purchase</option></select></Fld>
              {v.hardwareType !== 'none' && (<><Fld lbl="Model"><select value={v.hardwareModel} onChange={e=>setVoice('hardwareModel',e.target.value)} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}>{hardwareCatalog.yealink.map(m=><option key={m.id} value={m.id}>{m.label} — ${m.monthly}/mo or ${m.nrc}</option>)}</select></Fld><Fld lbl="Quantity"><NI v={v.hardwareQty} s={val=>setVoice('hardwareQty',val)}/></Fld>{freePhones && v.hardwareType === 'purchase' && (<div style={{ padding:'4px 7px', background:'#dcfce7', border:'1px solid #86efac', borderRadius:4, fontSize:9, color:'#166534', fontWeight:600 }}>✓ Hardware included FREE — 36-month bundle with {fmt$0(itBaseMRR)}/mo IT base</div>)}</>)}
              <Fld lbl="Programming Fee" sub="$25/seat"><NI v={v.programmingFee} s={val=>setVoice('programmingFee',val)}/></Fld></SubCard>
          </div>);
        case 'bundle':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Bundle discount')}{s2('Auto-applied when Managed IT and Voice ship together. The rate is driven by the contract term (IT — basis).')}
            {bundle?.qualifies ? (
              <div style={{ background: v3.color.inkBg, border: `1px solid ${v3.color.inkBorder}`, borderRadius: v3.radius.card, padding: 20 }}>
                <div style={{ fontSize: v3.font.size.label, fontWeight: 700, color: v3.color.ink }}>📦 Bundle discount applied</div>
                <div style={{ marginTop: 10, fontFamily: v3.font.mono, fontSize: 26, fontWeight: 600, color: v3.color.navy }}>{Math.round((bundle.rate || 0) * 100)}%<span style={{ fontSize: 14, color: v3.color.muted, marginLeft: 6 }}>off Voice MRR</span></div>
                <div style={{ marginTop: 6, fontSize: v3.font.size.body, color: v3.color.bodySoft }}>Saves the client <strong>{fmt$0(bundle.voiceDiscount || 0)}/mo</strong>{contractTerm ? ` on a ${contractTerm}-month term` : ''}.</div>
                {freePhones && <div style={{ marginTop: 10, padding: '8px 12px', background: v3.color.status.greenBg, borderRadius: v3.radius.input, fontSize: v3.font.size.small, color: v3.color.status.greenText, fontWeight: 600 }}>✓ Phones included free at this term + IT base MRR.</div>}
                <div style={{ marginTop: 12, fontSize: v3.font.size.small, color: v3.color.muted }}>Change the discount by adjusting the contract term under <strong>IT — basis</strong>.</div>
              </div>
            ) : s2('Bundle discount not yet qualified — set the Voice configuration and a qualifying contract term.')}
          </div>);
        case 'onboarding':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Onboarding')}{s2('Incentive, discounts, and the effective date.')}
            {bundleOnboardingLabor > 0 && (<OnboardingIncentive fee={bundleOnboardingLabor} marketTier={selectedMkt?.tier_key} contractTerm={contractTerm} initialValue={obIncentive} onChange={inc => { const sig = inc ? `${inc.mode || 'none'}:${inc.effectiveFee ?? ''}:${inc.discountPct ?? ''}:${inc.splitMonths ?? ''}` : 'null'; const prevSig = obIncentive ? `${obIncentive.mode || 'none'}:${obIncentive.effectiveFee ?? ''}:${obIncentive.discountPct ?? ''}:${obIncentive.splitMonths ?? ''}` : 'null'; if (sig === prevSig) return; setObIncentive(inc); }} />)}
            <ManualDiscounts value={manualDiscounts} onChange={setManualDiscounts} baseMRR={combinedMRRPreDiscount} />
            <OneTimeDiscounts value={oneTimeDiscounts} onChange={setOneTimeDiscounts} bases={oneTimeBases} />
            <AgreementEffectiveDate value={effectiveDate} onChange={setEffectiveDate} />
          </div>);
        case 'pricing':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Pricing')}{s2('Combined IT + Voice summary.')}
            <div style={{ background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.card, padding: 20 }}>
              <LI lbl="Managed IT — recurring" v={itResult?.finalMRR || 0} bold/>
              <LI lbl="Hosted Voice — recurring" v={voiceResultFinal?.finalMRR || 0} ind/>
              {bundle?.voiceDiscount > 0 && <LI lbl={`Bundle discount (${Math.round((bundle.rate || 0) * 100)}%)`} v={-(bundle.voiceDiscount || 0)} ind/>}
              <LI lbl="Combined monthly recurring" v={combinedMRR} hi/>
              <div style={{ height: 1, background: v3.color.divider, margin: '10px 0' }} />
              <LI lbl="One-time (NRC)" v={combinedNRC} ind/>
              <LI lbl="Combined monthly cost" v={combinedCost} ind muted/>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', background: gmBg(combinedGM), borderRadius: 4, marginTop: 6 }}><span style={{ fontSize: 12, fontWeight: 700, color: gmColor(combinedGM) }}>Implied Gross Margin</span><span style={{ fontSize: 14, fontWeight: 700, fontFamily: v3.font.mono, color: gmColor(combinedGM) }}>{fmtPct(combinedGM)}</span></div>
              <LI lbl="Total Contract Value" v={combinedTCV} bold/>
            </div>
          </div>);
        case 'documents':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Documents & send')}{s2('Lock rates, send the Bundle agreement for signature, and collect prepayment.')}
            <QuoteDocuments quoteId={existingQuote?.id} />
            <div id="oos-rate-card"><MarketRateCard quoteId={existingQuote?.id} clientZip={clientZip} fallbackMarket={selectedMkt}
              onRatesAccepted={(rates, suggestedTier, analysis) => { if (rates) setAcceptedRates(rates); if (suggestedTier && marketTiers.length) { const tier = marketTiers.find(t => t.tier_key === suggestedTier); if (tier) setSelectedMkt(tier); } if (analysis?.pricing_multiplier != null) { setPendingMultiplier({ multiplier: analysis.pricing_multiplier, tier: analysis.market_tier, city: analysis.city, state: analysis.state }); setShowMktRecommend(true); } if (analysis) setMarketAnalysis({ ...analysis, rates: rates || analysis.rates }); }} /></div>
            {existingQuote?.id && (<SignatureCard docType="bundle_quote" ref={signatureCardRef} quoteId={existingQuote.id} quoteStatus={quoteStatus} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} clientContact={recipientContact} clientEmail={recipientEmail} upfrontAmount={itResult ? oneTimeResolved.finalNRC : 0} userId={profile?.id} ratesLocked={ratesLocked} onLockRatesPrompt={() => setShowLockRatesModal('send_signature')} />)}
            <DocumentsPanel analysis={marketAnalysis || (zipResult ? { city: marketCity, state: marketState, market_tier: zipResult.tier, pricing_multiplier: 1, rates: {} } : null)} settings={settings} clientName={recipientBiz} recipientContact={recipientContact} quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} sptProposalId={sptProposalId} onSPTLinked={(pid) => setSptProposalId(pid)} inputs={itInputs} pkg={selectedPkg} products={products} complianceKey={(({ hipaa:['hipaa'], soc2:['soc2'], pci:['pci'], cmmc:['cmmc'], moderate:['hipaa','soc2'], high:['pci','cmmc'] })[itInputs?.compliance] || [])} result={itResult ? { finalMRR: bundle?.finalMRR || 0, onboarding: itResult.onboarding + (voiceResultFinal?.nrc || 0) } : null} obIncentive={obIncentive} quoteType="bundle" clientEmail={recipientEmail} prepayAmount={oneTimeResolved.finalNRC} hubspotDealId={hubDealId} onOpenAssumptions={() => setShowAssumptions(true)} onOpenAddendums={() => setAddendumsModalOpen(true)} addendumsCount={addendums.length} onOpenServiceOrderPreview={() => setSoPreviewModalOpen(true)} assumptionsSaved={assumptionsComplete} />
            {existingQuote?.id && (<div><V3Button variant="secondary" size="sm" onClick={() => setShowEtfModal(true)}>💰 Calculate ETF →</V3Button></div>)}
          </div>);
        case 'terms':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Special terms')}{s2('Addendums attached to the Bundle agreement.')}<div style={{ fontSize: v3.font.size.body, color: v3.color.muted }}>{addendums.length} addendum{addendums.length === 1 ? '' : 's'} attached.</div><div><V3Button variant="secondary" size="sm" onClick={() => setAddendumsModalOpen(true)}>Edit addendums</V3Button></div></div>);
        case 'engagement':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Engagement')}{s2('Client interaction with the sent quote (live).')}<EngagementPanel quoteId={existingQuote?.id} /><QuoteNotes quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} hubDealId={hubDealId} /></div>);
        case 'history':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('History')}{s2('Saved versions of this quote.')}<QuoteHistory quoteId={existingQuote?.id} /></div>);
        default:
          return null;
      }
    };

    const locked = isQuoteLocked(existingQuote);
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        {isReadOnly && (<div style={{ padding: '8px 24px', background: v3.color.status.amberBg, color: v3.color.status.amberText, fontSize: 12, fontWeight: 600 }}>🔒 View only — your account no longer has permission to edit Bundle quotes.</div>)}
        {locked && (
          <LockedQuoteBanner notes={dealDescription} onNotesChange={setDealDescription} onSave={saveQuote} saving={saving} reason={QUOTE_LOCK_REASON} />
        )}
        <WorkspaceShell header={v3Header} sections={v3Sections} activeId={activeSection} onSelect={setBundleFocusedSection} rail={v3Rail}>
          {locked && activeSection !== 'documents'
            ? <div style={{ pointerEvents: 'none', opacity: 0.55 }} aria-disabled="true">{renderBundleFocus()}</div>
            : renderBundleFocus()}
        </WorkspaceShell>
        {showAssumptions && (<AssumptionsModal onClose={() => setShowAssumptions(false)} quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} recipientContact={recipientContact} inputs={itInputs} pkg={selectedPkg} products={products} settings={settings} onSave={(a) => { setAssumptionsStory?.(a.story || ''); }} />)}
        <AddendumsModal open={addendumsModalOpen} onClose={() => setAddendumsModalOpen(false)} addendums={addendums} templates={addendumTemplates} onSave={setAddendums} />
        <ServiceOrderPreviewModal open={soPreviewModalOpen} onClose={() => setSoPreviewModalOpen(false)} quoteId={existingQuote?.id} docType="bundle_quote" clientBiz={recipientBiz} />
        {showEtfModal && (<ETFCalculatorModal onClose={() => setShowEtfModal(false)} defaultMRR={combinedMRR || ''} defaultTerm={contractTerm || 36} defaultClientName={recipientBiz} defaultClientAddress={recipientAddress} defaultClientContact={recipientContact} defaultClientEmail={recipientEmail} defaultContractReference={existingQuote?.quote_number || ''} quoteId={existingQuote?.id} hubDealId={hubDealId} />)}
        {showLockRatesModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 480, boxShadow: v3.shadow.modal }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: v3.color.status.amberText, margin: '0 0 10px' }}>⚠ Lock OOS Rates First?</h3>
              <p style={{ fontSize: 12, color: v3.color.bodySoft, lineHeight: 1.6, margin: '0 0 14px' }}>The Out-of-Scope Rate Schedule isn&rsquo;t locked yet. Open <strong>Documents &amp; send</strong> and click <em>Accept All Rates</em> before sending.</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <V3Button variant="secondary" size="sm" onClick={() => setShowLockRatesModal(null)}>Cancel</V3Button>
                <V3Button variant="secondary" size="sm" onClick={() => { const action = showLockRatesModal; setShowLockRatesModal(null); if (action === 'send_signature') signatureCardRef.current?.openSendModal(); }}>Send Anyway</V3Button>
                <V3Button variant="dark" size="sm" onClick={() => { setShowLockRatesModal(null); setBundleFocusedSection('documents'); setTimeout(scrollToOosRateCard, 60); }}>Go to Rates ↓</V3Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
    {/* ── Unbundle Modal ──────────────────────────────────────────────── */}
    {showUnbundle && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:400 }}>
        <div style={{ background:'white', borderRadius:10, padding:28, width:440, boxShadow:'0 8px 32px rgba(0,0,0,0.15)' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:'0 0 6px' }}>Unbundle Quote</h3>
          <p style={{ fontSize:12, color:'#6b7280', margin:'0 0 20px' }}>
            Choose which service to keep as a standalone quote. The bundle quote will remain in your history.
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>
            <button onClick={() => handleUnbundle('it')} disabled={unbundling}
              style={{ padding:'12px 16px', background:'#f0f4ff', border:'2px solid #2563eb', borderRadius:7, cursor:'pointer', textAlign:'left', opacity: unbundling ? 0.6 : 1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#1e40af', marginBottom:3 }}>🖥 Keep Managed IT Only</div>
              <div style={{ fontSize:11, color:'#3b82f6' }}>Opens a new IT quote with all device, user, and product settings intact. Voice section removed.</div>
            </button>
            <button onClick={() => handleUnbundle('voice')} disabled={unbundling}
              style={{ padding:'12px 16px', background:'#faf5ff', border:'2px solid #7c3aed', borderRadius:7, cursor:'pointer', textAlign:'left', opacity: unbundling ? 0.6 : 1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#6d28d9', marginBottom:3 }}>📞 Keep Hosted Voice Only</div>
              <div style={{ fontSize:11, color:'#7c3aed' }}>Opens a new Voice quote with all seat, license, and hardware settings intact. IT section removed.</div>
            </button>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:8, alignItems:'center' }}>
            {unbundling && <span style={{ fontSize:11, color:'#6b7280' }}>Creating new quote...</span>}
            <button onClick={() => setShowUnbundle(false)} disabled={unbundling}
              style={{ padding:'7px 16px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:12, color:'#374151', cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>

      {/* ── LEFT: IT + Voice inputs ── */}
      <div style={{ width:360, flexShrink:0, background:'white', borderRight:'1px solid #e5e7eb', overflowY:'auto', padding:'12px 14px' }}>

        {/* v3.5.67: read-only banner */}
        {isReadOnly && (
          <div style={{ marginBottom:8, padding:'8px 10px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:5, fontSize:11, color:'#92400e', fontWeight:600 }}>
            🔒 View only — your account no longer has permission to edit Bundle quotes. Contact an admin to restore access.
          </div>
        )}

        {/* Bundle badge */}
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10, padding:'6px 10px', background:'#f0f4ff', border:'1px solid #c4b5fd', borderRadius:6 }}>
          <span style={{ fontSize:14 }}>📦</span>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#4c1d95' }}>Bundle Quote</div>
            <div style={{ fontSize:9, color:'#7c3aed' }}>Managed IT + Voice · bundle discount auto-applied</div>
          </div>
          {existingQuote && <span style={{ marginLeft:'auto', fontSize:10, fontFamily:'DM Mono, monospace', fontWeight:700, color:'#1e40af' }}>{existingQuote.quote_number}</span>}
        </div>

        {/* HubSpot */}
        <HubSpotConnect
          dealId={hubDealId}
          dealUrl={hubDealUrl}
          dealName={hubDealName}
          description={dealDescription}
          onDescriptionChange={setDealDescription}
          quoteNumber={existingQuote?.quote_number}
          mrr={bundle?.finalMRR}
          contractValue={bundle?.contractValue}
          packageName={selectedPkg?.name}
          contractTerm={contractTerm}
          existingQuoteId={existingQuote?.id}
          clientName={recipientBiz}
          onConnect={full => {
            setHubDealId(full.dealId); setHubDealUrl(full.dealUrl); setHubDealName(full.deal.dealname);
            if (full.company) {
              if (full.company.name) setRecipientBiz(full.company.name);
              const addr = [full.company.address, full.company.city, full.company.state, full.company.zip].filter(Boolean).join(', ');
              if (addr) setRecipientAddress(addr);
              if (full.company.zip) { setClientZip(full.company.zip); const zr = lookupZip(full.company.zip); setZipResult(zr); if (zr) applyZip(zr); }
            } else { const x = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim(); if (x) setRecipientBiz(x); }
            if (full.contact) { const n=[full.contact.firstname,full.contact.lastname].filter(Boolean).join(' '); if(n) setRecipientContact(n); if(full.contact.email) setRecipientEmail(full.contact.email); if(full.contact.phone) setRecipientPhone(full.contact.phone); if(full.contact.mobilephone) setRecipientMobilePhone(full.contact.mobilephone); }
      // v3.5.99.7.4: capture write-back routing IDs + original-state snapshot
      if (full.contactId) setHubContactId(full.contactId);
      if (full.companyId) setHubCompanyId(full.companyId);
      setHubspotSnapshot(captureHubspotSnapshot(full));
            if (!proposalName && full.deal.dealname) setProposalName(`FerrumIT Bundle — ${full.company?.name||full.deal.dealname}`);
          }}
          onDisconnect={() => { setHubDealId(''); setHubDealUrl(''); setHubDealName(''); }}
        />
        <SPTConnect
          proposalId={sptProposalId}
          quoteId={existingQuote?.id}
          clientName={recipientBiz}
          quoteNumber={existingQuote?.quote_number}
          settings={settings}
          onConnect={(pid) => setSptProposalId(pid)}
          onDisconnect={() => setSptProposalId(null)}
        />

        {/* Client fields */}
        <Sec t="Proposal Details" c="#0f1e3c">
          <Fld lbl="Assigned Sales Rep">
            <RepSelector repId={repId} setRepId={setRepId} teamMembers={teamMembers} />
          </Fld>
          <Fld lbl="Proposal Name"><TI value={proposalName} onChange={setProposalName} placeholder="FerrumIT Bundle — Acme Corp"/></Fld>
          <Fld lbl="Client Business Name"><TI value={recipientBiz} onChange={setRecipientBiz} placeholder="Acme Corp"/></Fld>
          <Grid2>
            <Fld lbl="Contact Name"><TI value={recipientContact} onChange={setRecipientContact} placeholder="Jane Smith"/></Fld>
            <Fld lbl="Contact Email"><TI value={recipientEmail} onChange={setRecipientEmail} placeholder="jane@acme.com"/></Fld>
          </Grid2>
          <Fld lbl="Business Address"><TI value={recipientAddress} onChange={setRecipientAddress} placeholder="123 Main St, Chicago, IL 60601"/></Fld>
          <Grid2>
            <Fld lbl="Office Phone"><TI value={recipientPhone} onChange={setRecipientPhone} placeholder="(555) 123-4567"/></Fld>
            <Fld lbl="Mobile Phone"><TI value={recipientMobilePhone} onChange={setRecipientMobilePhone} placeholder="(555) 765-4321"/></Fld>
          </Grid2>
          {/* v3.5.99.7.6: manual full-overwrite push to HubSpot */}
          {(hubContactId || hubCompanyId) && (
            <div style={{ marginTop: 6, padding: '6px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={pushAllToHubspot} disabled={hubPushing}
                style={{ padding: '4px 10px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: hubPushing ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                {hubPushing ? '⏳ Pushing...' : '↑ Push to HubSpot'}
              </button>
              <span style={{ fontSize: 9, color: '#0c4a6e' }}>Overwrites HubSpot contact + company with values above</span>
              {hubPushMsg && <span style={{ fontSize: 10, color: '#075985', flex: 1, textAlign: 'right', minWidth: 0 }}>{hubPushMsg}</span>}
            </div>
          )}
          {/* v3.5.99.0: Internal Notification CCs — see QuotePage block for rationale */}
          <div style={{ marginTop: 10, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#92400e', marginBottom: 4 }}>🔒 Internal Notifications</div>
            <Fld lbl="CC on Approval (comma-separated)"><TI value={notificationCc} onChange={setNotificationCc} placeholder="alice@ferrumit.com, bob@ferrumit.com"/></Fld>
            <div style={{ fontSize: 9, color: '#92400e', marginTop: 4, lineHeight: 1.4 }}>Leave blank to use the global default from Admin → Notifications. Not visible to the customer.</div>
          </div>
          <Fld lbl="Zip Code">
            <div style={{ display:'flex', gap:5, alignItems:'center' }}>
              <input value={clientZip} onChange={e=>handleZipChange(e.target.value)} placeholder="60601"
                style={{ flex:1, padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', outline:'none' }}/>
              {zipResult && !zipApplied && (
                <button onClick={()=>applyZip()} style={{ padding:'3px 7px', background:tierColor(zipResult.tier), color:'white', border:'none', borderRadius:3, fontSize:9, fontWeight:700, cursor:'pointer' }}>Apply →</button>
              )}
            </div>
            {zipResult && <div style={{ fontSize:9, color:'#6b7280', marginTop:2 }}>{zipResult.name}{zipApplied ? ' ✓' : ''}</div>}
          </Fld>
        </Sec>

        {/* ── MANAGED IT SECTION ── */}
        <CollapsibleSec
          title="🖥 Managed IT"
          open={itOpen} onToggle={() => setItOpen(o=>!o)}
          badge={itResult ? fmt$0(itResult.finalMRR) + '/mo' : null}
          color="#2563eb">

          {/* Market tier */}
          <Sec t="Market Tier" c="#0f1e3c">
            {/* AI recommendation banner — shown when MarketRateCard has surfaced
                a fresh analysis the rep hasn't yet accepted (v3.5.38). Mirrors
                QuotePage. */}
            {showMktRecommend && pendingMultiplier && (
              <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:5, padding:'7px 9px', marginBottom:6 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#1e40af', marginBottom:3 }}>
                  📊 AI Market Recommendation
                </div>
                <div style={{ fontSize:9, color:'#1e40af', marginBottom:5 }}>
                  <strong>{pendingMultiplier.city}, {pendingMultiplier.state}</strong> — {pendingMultiplier.tier?.charAt(0).toUpperCase() + pendingMultiplier.tier?.slice(1)} market
                  · <strong>{pendingMultiplier.multiplier === 1 ? 'No adjustment' : pendingMultiplier.multiplier > 1 ? `+${Math.round((pendingMultiplier.multiplier - 1) * 100)}%` : `-${Math.round((1 - pendingMultiplier.multiplier) * 100)}%`}</strong>
                </div>
                <div style={{ display:'flex', gap:4 }}>
                  <button onClick={() => {
                    setAiMultiplier(pendingMultiplier.multiplier);
                    setAiMultiplierTier(pendingMultiplier.tier);
                    setShowMktRecommend(false);
                  }} style={{ padding:'3px 10px', background:'#1e40af', color:'white', border:'none', borderRadius:3, fontSize:9, fontWeight:700, cursor:'pointer' }}>
                    Apply AI Rate
                  </button>
                  <button onClick={() => setShowMktRecommend(false)}
                    style={{ padding:'3px 7px', background:'white', border:'1px solid #d1d5db', borderRadius:3, fontSize:9, color:'#6b7280', cursor:'pointer' }}>
                    Keep Manual
                  </button>
                </div>
              </div>
            )}

            {/* Market status badge (v3.5.37 / v3.5.38) — now reflects live AI
                multiplier state on Bundle since v3.5.38 wired up the override flow. */}
            <div style={{ marginBottom: 6 }}>
              <MarketStatusBadge
                aiMultiplier={aiMultiplier}
                aiMultiplierTier={aiMultiplierTier}
                selectedMkt={selectedMkt}
                marketCity={marketCity}
                marketState={marketState}
                locked={!!pricingSnapshot}
                compact={true}
                onRunAnalysis={aiMultiplier != null ? (() => {
                  setAiMultiplier(null);
                  setAiMultiplierTier(null);
                }) : undefined}
              />
            </div>
            {marketTiers.map(t => (
              <div key={t.id} onClick={() => { setSelectedMkt(t); setAiMultiplier(null); setAiMultiplierTier(null); }} style={{ padding:'4px 7px', borderRadius:4, cursor:'pointer', marginBottom:2, border:`${selectedMkt?.id===t.id?'2':'1'}px solid ${selectedMkt?.id===t.id?tierColor(t.tier_key):'#e5e7eb'}`, background:selectedMkt?.id===t.id?tierBg(t.tier_key):'white', display:'flex', justifyContent:'space-between', alignItems:'center', opacity: aiMultiplier != null ? 0.55 : 1 }}>
                <span style={{ fontSize:9, fontWeight:700, color:tierColor(t.tier_key) }}>{staticTierToMILabel(t.tier_key)} <span style={{ fontWeight:400, color:'#9ca3af' }}>({t.name})</span></span>
                <span style={{ fontSize:8, color:'#6b7280', fontFamily:'DM Mono, monospace' }}>classification</span>
              </div>
            ))}
            ))}
          </Sec>

          {/* Package */}
          <Sec t="Package" c="#2563eb">
            {packages.map(p => (
              <div key={p.id} onClick={() => setSelectedPkg(p)} style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', marginBottom:2, border:`${selectedPkg?.id===p.id?'2':'1'}px solid ${selectedPkg?.id===p.id?'#2563eb':'#e5e7eb'}`, background:selectedPkg?.id===p.id?'#eff6ff':'white' }}>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:9, fontWeight:700, color:selectedPkg?.id===p.id?'#1e40af':'#374151' }}>{p.name}</span>
                  <span style={{ fontSize:8, fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${p.ws_rate}/WS · ${p.user_rate}/US</span>
                </div>
              </div>
            ))}
            <Fld lbl="Quote Status" s={{ marginTop:8 }}>
              <SI v={quoteStatus} s={setQuoteStatus} opts={[['draft','Draft'],['in_review','In Review'],['approved','Approved'],['sent','Sent'],['won','Won'],['lost','Lost'],['expired','Expired']]}/>
            </Fld>
          </Sec>

          {/* People & devices */}
          <Sec t="People & Devices" c="#7c3aed">
            <Grid2>
              <Fld lbl="Users"><NI v={itInputs.users} s={val=>setIt('users',val)}/></Fld>
              <Fld lbl="Shared Mailboxes"><NI v={itInputs.sharedMailboxes} s={val=>setIt('sharedMailboxes',val)}/></Fld>
              <Fld lbl="Workstations"><NI v={itInputs.workstations} s={val=>setIt('workstations',val)}/></Fld>
              <Fld lbl="Total Devices"><NI v={itInputs.endpoints} s={val=>setIt('endpoints',val)}/></Fld>
              <Fld lbl="Mobile Devices"><NI v={itInputs.mobileDevices||0} s={val=>setIt('mobileDevices',val)}/></Fld>
            </Grid2>
          </Sec>

          {/* Infrastructure */}
          <Sec t="Infrastructure" c="#0891b2">
            <Grid2>
              <Fld lbl="Servers"><NI v={itInputs.servers} s={val=>setIt('servers',val)}/></Fld>
              <Fld lbl="Locations"><NI v={itInputs.locations} s={val=>setIt('locations',val)}/></Fld>
              <Fld lbl="Cloud Tenants"><NI v={itInputs.cloudTenants} s={val=>setIt('cloudTenants',val)}/></Fld>
              {/* v3.5.96.0: Domains — same slot as QuotePage (after Cloud Tenants). Drives
                  per-domain product qty + onboarding fee on the IT side of the Bundle. */}
              {/* v3.5.99.7.11: DomainsInfo bubble — see QuotePage. */}
              <Fld lbl="Domains">
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ flex: 1 }}><NI v={itInputs.domains} s={val=>setIt('domains',val)}/></div>
                  <DomainsInfo />
                </div>
              </Fld>
              <Fld lbl="Vendors"><NI v={itInputs.vendors} s={val=>setIt('vendors',val)}/></Fld>
            </Grid2>
          </Sec>

          {/* Risk */}
          <Sec t="Risk & Compliance" c="#dc2626">
            <Grid2>
              {/* v3.5.59: 'Industry Risk' renamed to 'Threat Profile'; values
                  Low/Medium/High → Standard/Elevated/Critical (data keys preserved). */}
              <Fld lbl="Threat Profile" sub="oversight tier">
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ flex: 1 }}><SI v={itInputs.industryRisk} s={val=>setIt('industryRisk',val)} opts={[['none','None'],['low','Standard'],['medium','Elevated'],['high','Critical']]}/></div>
                  <ThreatProfileInfo />
                </div>
              </Fld>
              {/* v3.5.59: Compliance expanded to 5 framework values; matching
                  Compliance Program product is auto-recommended. */}
              <Fld lbl="Compliance" sub="adds program product"><SI v={itInputs.compliance} s={val=>setIt('compliance',val)} opts={[['none','None'],['hipaa','HIPAA'],['soc2','SOC 2'],['pci','PCI DSS'],['cmmc','CMMC']]}/></Fld>
              <Fld lbl="Complexity"><SI v={itInputs.complexity} s={val=>setIt('complexity',val)} opts={[['low','Low'],['medium','Medium'],['high','High']]}/></Fld>
              <Fld lbl="Coverage"><SI v={itInputs.requestedCoverage} s={val=>setIt('requestedCoverage',val)} opts={[['business_hours','8×5'],['24x5','24×5'],['24x7','24×7']]}/></Fld>
            </Grid2>
          </Sec>

          {/* Add-ons */}
          {Object.entries(productsByCategory).map(([cat, catProducts]) => {
            // v3.5.40: hide internal-only products from rep-facing picker
            const visibleProducts = catProducts.filter(p => !p.internal_only);
            if (visibleProducts.length === 0) return null;
            return (
            <Sec key={cat} t={cat} c="#374151">
              {visibleProducts.map(p => {
                const sel = isSelected(p.id);
                const qty = parseInt(itInputs.manualQuantities?.[p.id] || 0);
                return (
                  <div key={p.id} onClick={() => toggleProduct(p.id)}
                    style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 6px', borderRadius:3, cursor:'pointer', marginBottom:2, border:`1px solid ${sel?'#93c5fd':'#e5e7eb'}`, background:sel?'#eff6ff':'white' }}>
                    <div style={{ width:20, height:12, borderRadius:6, flexShrink:0, background:sel?'#2563eb':'#d1d5db', position:'relative' }}>
                      <div style={{ position:'absolute', top:2, left:sel?10:2, width:8, height:8, borderRadius:'50%', background:'white', transition:'left .1s' }}/>
                    </div>
                    <span style={{ fontSize:9, fontWeight:600, flex:1, color:sel?'#1e40af':'#374151' }}>{p.name}</span>
                    {sel && p.qty_driver === 'manual' && (
                      <div onClick={e=>e.stopPropagation()} style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:0 }}>
                        <input type="number" min="0" value={qty || ''} placeholder="qty"
                          onChange={e=>{
                            const next = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0);
                            setItInputs(prev => ({ ...prev, manualQuantities: { ...(prev.manualQuantities || {}), [p.id]: next } }));
                          }}
                          style={{ width:48, padding:'1px 4px', border:`1px solid ${qty > 0 ? '#93c5fd' : '#fbbf24'}`, borderRadius:3, fontSize:9, fontFamily:'DM Mono, monospace', textAlign:'center', outline:'none', background: qty > 0 ? 'white' : '#fffbeb' }}/>
                        {qty === 0 && <span style={{ fontSize:7, color:'#92400e', fontWeight:600, marginTop:1 }}>set qty</span>}
                      </div>
                    )}
                    <span style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${p.sell_price}/{p.qty_driver === 'manual' ? 'lic' : p.qty_driver}</span>
                  </div>
                );
              })}
            </Sec>
            );
          })}

          {/* Contract term (shared — drives bundle discount) */}
          {/* v3.5.86.2.2: card sub-labels derived from pricing_settings via
              getBundleVoiceDiscOptions(settings). Reads bundle_voice_disc_${term}
              and bundle_free_phones_min_term so admin changes surface in the
              UI immediately. The "+ free phones" suffix shows on whichever
              term equals bundle_free_phones_min_term (default 36). */}
          <Sec t="Contract Term" c="#374151">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4 }}>
              {getBundleVoiceDiscOptions(settings).map(([term,lbl,sub])=>(
                <div key={term} onClick={()=>setIt('contractTerm',term)} style={{ padding:'6px 5px', borderRadius:4, cursor:'pointer', textAlign:'center', border:`${itInputs.contractTerm===term?'2':'1'}px solid ${itInputs.contractTerm===term?'#7c3aed':'#e5e7eb'}`, background:itInputs.contractTerm===term?'#faf5ff':'white' }}>
                  <div style={{ fontSize:10, fontWeight:700, color:itInputs.contractTerm===term?'#6d28d9':'#374151' }}>{lbl}</div>
                  <div style={{ fontSize:8, color:'#9ca3af', marginTop:1 }}>{sub}</div>
                </div>
              ))}
            </div>
          </Sec>
        </CollapsibleSec>

        {/* ── VOICE SECTION ── */}
        <CollapsibleSec
          title="📞 Hosted Voice"
          open={voiceOpen} onToggle={() => setVoiceOpen(o=>!o)}
          badge={voiceDiscountedMRR > 0 ? fmt$0(voiceDiscountedMRR) + '/mo' : null}
          color="#7c3aed">

          {/* Quote type */}
          <Sec t="Voice Type" c="#7c3aed">
            <div style={{ display:'grid', gap:3 }}>
              {[['hosted','Hosted Voice — Per Seat'],['hybrid','Hybrid Hosting — BYOPBX'],['sip','SIP Trunking Only']].map(([key,lbl])=>(
                <div key={key} onClick={()=>setVoice('quoteType',key)} style={{ padding:'5px 7px', borderRadius:4, cursor:'pointer', border:`${v.quoteType===key?'2':'1'}px solid ${v.quoteType===key?'#7c3aed':'#e5e7eb'}`, background:v.quoteType===key?'#faf5ff':'white' }}>
                  <span style={{ fontSize:9, fontWeight:700, color:v.quoteType===key?'#6d28d9':'#374151' }}>{lbl}</span>
                </div>
              ))}
            </div>
          </Sec>

          {v.quoteType === 'hosted' && (
            <Sec t="Seats" c="#0891b2">
              <Grid2>
                <Fld lbl="Billable Seats"><NI v={v.seats} s={val=>setVoice('seats',val)}/></Fld>
                <Fld lbl="Sell Price/Seat ($)"><NI v={v.seatPrice} s={val=>setVoice('seatPrice',val)}/></Fld>
              </Grid2>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4, marginTop:4 }}>
                {[['pro','3CX Pro'],['enterprise','3CX Enterprise']].map(([key,lbl])=>(
                  <div key={key} onClick={()=>setVoice('licenseType',key)} style={{ padding:'5px 6px', borderRadius:3, cursor:'pointer', textAlign:'center', border:`${v.licenseType===key?'2':'1'}px solid ${v.licenseType===key?'#2563eb':'#e5e7eb'}`, background:v.licenseType===key?'#eff6ff':'white', gridColumn: key==='pro'?'1/2':'2/4' }}>
                    <div style={{ fontSize:9, fontWeight:700, color:v.licenseType===key?'#1e40af':'#374151' }}>{lbl}</div>
                  </div>
                ))}
              </div>
              <Sec t="Non-Billable Devices" c="#6b7280">
                <Grid2>
                  <Fld lbl="Common Area"><NI v={v.commonAreaPhones} s={val=>setVoice('commonAreaPhones',val)}/></Fld>
                  <Fld lbl="Voicemail Only"><NI v={v.voicemailOnly} s={val=>setVoice('voicemailOnly',val)}/></Fld>
                  <Fld lbl="Door Phones"><NI v={v.doorPhones} s={val=>setVoice('doorPhones',val)}/></Fld>
                  <Fld lbl="Paging"><NI v={v.pagingDevices} s={val=>setVoice('pagingDevices',val)}/></Fld>
                </Grid2>
              </Sec>
            </Sec>
          )}

          {(v.quoteType === 'hybrid' || v.quoteType === 'sip') && (
            <Sec t="SIP Channels" c="#0891b2">
              <Fld lbl="Concurrent Call Paths" sub="$19.95/path/mo"><NI v={v.sipChannels} s={val=>setVoice('sipChannels',val)}/></Fld>
              {v.quoteType === 'hybrid' && (
                <>
                  <Fld lbl="3CX License Tier" style={{ marginTop:4 }}>
                    <select value={v.cxTierId} onChange={e=>setVoice('cxTierId',e.target.value)}
                      style={{ width:'100%', padding:'4px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}>
                      {/* v3.5.77: dropdown rendered from cxTiersDB so admin price edits flow through. */}
                      {(() => {
                        const tiers = (Array.isArray(cxTiersDB) && cxTiersDB.length > 0) ? cxTiersDB : CX_TIERS;
                        const fmtCost = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                        const proTiers = tiers.filter(t => t.type === 'pro');
                        const entTiers = tiers.filter(t => t.type === 'enterprise');
                        return (
                          <>
                            <optgroup label="Pro">
                              {proTiers.map(t => (
                                <option key={t.id} value={t.id}>Pro {t.paths} paths — ${fmtCost(t.annual_cost)}/yr</option>
                              ))}
                            </optgroup>
                            <optgroup label="Enterprise">
                              {entTiers.map(t => (
                                <option key={t.id} value={t.id}>Ent. {t.paths} paths — ${fmtCost(t.annual_cost)}/yr</option>
                              ))}
                            </optgroup>
                          </>
                        );
                      })()}
                    </select>
                  </Fld>
                  <Tog on={v.clientPaysMonthly} set={val=>setVoice('clientPaysMonthly',val)} lbl="Amortize license monthly"/>
                  <Tog on={v.largerInstance} set={val=>setVoice('largerInstance',val)} lbl="Enhanced VM instance"/>
                </>
              )}
            </Sec>
          )}

          <Sec t="Numbers & DIDs" c="#0f766e">
            <Grid2>
              <Fld lbl="Local DIDs" sub="$1.50"><NI v={v.localDIDs} s={val=>setVoice('localDIDs',val)}/></Fld>
              <Fld lbl="SMS DIDs" sub="+$1.25"><NI v={v.smsDIDs} s={val=>setVoice('smsDIDs',val)}/></Fld>
              <Fld lbl="Toll-Free" sub="$5.00"><NI v={v.tollFreeNumbers} s={val=>setVoice('tollFreeNumbers',val)}/></Fld>
              <Fld lbl="E911 DIDs" sub="$2.50"><NI v={v.e911DIDs} s={val=>setVoice('e911DIDs',val)}/></Fld>
              <Fld lbl="Port Numbers" sub="$25 NRC"><NI v={v.portingNumbers} s={val=>setVoice('portingNumbers',val)}/></Fld>
            </Grid2>
          </Sec>

          <Sec t="Fax & Add-ons" c="#0891b2">
            <Fld lbl="Fax Package">
              <select value={v.faxType} onChange={e=>setVoice('faxType',e.target.value)}
                style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}>
                <option value="none">No Fax</option>
                {Object.entries(getFaxPackages(faxPackagesDB)).map(([key, pkg]) => (
                  <option key={key} value={key}>{pkg.label} — ${pkg.price.toFixed(2)}/mo</option>
                ))}
              </select>
            </Fld>
            {/* Live GM display for the selected fax package */}
            {v.faxType && v.faxType !== 'none' && (() => {
              const fp = getFaxPackages(faxPackagesDB)[v.faxType];
              if (!fp) return null;
              const gm = fp.price > 0 ? ((fp.price - (fp.cost || 0)) / fp.price) * 100 : 0;
              const gmCol = gm >= 50 ? '#065f46' : gm >= 25 ? '#92400e' : '#991b1b';
              const noCost = !fp.cost || fp.cost === 0;
              return (
                <div style={{ marginTop:4, padding:'4px 7px', background: noCost ? '#fef3c7' : '#f0fdf4', border:`1px solid ${noCost ? '#fde68a' : '#a7f3d0'}`, borderRadius:4, fontSize:9, color: noCost ? '#92400e' : '#065f46', display:'flex', justifyContent:'space-between' }}>
                  <span>Sell ${fp.price.toFixed(2)} · Cost ${(fp.cost||0).toFixed(2)}</span>
                  <span style={{ fontWeight:700, color:gmCol }}>{gm.toFixed(0)}% GM{noCost ? ' (cost not set)' : ''}</span>
                </div>
              );
            })()}
            <Tog on={v.callRecording} set={val=>setVoice('callRecording',val)} lbl="Call Recording" sub="$15/mo"/>
            <Tog on={v.smsEnabled} set={val=>setVoice('smsEnabled',val)} lbl="SMS/MMS" sub="10DLC + metered"/>
          </Sec>

          <Sec t="Hardware" c="#374151">
            <Fld lbl="Hardware Type">
              <select value={v.hardwareType} onChange={e=>setVoice('hardwareType',e.target.value)}
                style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}>
                <option value="none">No Hardware</option>
                <option value="lease">Evergreen Lease</option>
                <option value="purchase">Outright Purchase</option>
              </select>
            </Fld>
            {v.hardwareType !== 'none' && (
              <>
                <Fld lbl="Model">
                  <select value={v.hardwareModel} onChange={e=>setVoice('hardwareModel',e.target.value)}
                    style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:10, background:'white', outline:'none' }}>
                    {hardwareCatalog.yealink.map(m=><option key={m.id} value={m.id}>{m.label} — ${m.monthly}/mo or ${m.nrc}</option>)}
                  </select>
                </Fld>
                <Fld lbl="Quantity"><NI v={v.hardwareQty} s={val=>setVoice('hardwareQty',val)}/></Fld>
                {freePhones && v.hardwareType === 'purchase' && (
                  <div style={{ padding:'4px 7px', background:'#dcfce7', border:'1px solid #86efac', borderRadius:4, fontSize:9, color:'#166534', fontWeight:600 }}>
                    ✓ Hardware included FREE — 36-month bundle with ${fmt$0(itBaseMRR)}/mo IT base
                  </div>
                )}
              </>
            )}
            <Fld lbl="Programming Fee" sub="$25/seat"><NI v={v.programmingFee} s={val=>setVoice('programmingFee',val)}/></Fld>
          </Sec>
        </CollapsibleSec>

        {/* Save */}
        <QuoteSaveBar
          onSave={saveQuote}
          existingQuote={existingQuote}
          quoteStatus={quoteStatus}
          setQuoteStatus={setQuoteStatus}
          quoteType="bundle"
          saving={saving}
          saveMsg={saveMsg}
          saveLabelNew="Save Bundle Quote"
          saveLabelExisting="Update Bundle Quote"
          saveButtonPadding="7px"
          reviewRowMarginTop={5}
          extraButtons={
            // v3.5.86.3: Customer Story & Assumptions entry point. Mirrors
            // QuotePage's v3.5.81.6 / MultiSite's v3.5.86.2 patterns —
            // captures the combined IT + Voice discovery context. Disabled
            // until quote is saved (assumptions persist on inputs.assumptions,
            // FK required). Status indicator (✓ green vs ⚠ amber) shows at a
            // glance whether the customer story has been captured.
            <button
              onClick={() => setShowAssumptions(true)}
              disabled={!existingQuote?.id}
              title={!existingQuote?.id ? 'Save the quote first' : (assumptionsComplete ? 'Customer Story captured — click to review or edit' : 'Capture combined IT + Voice discovery context')}
              style={{
                padding:'4px 8px',
                background: assumptionsComplete ? '#f0fdf4' : '#fffbeb',
                border: `1px solid ${assumptionsComplete ? '#bbf7d0' : '#fde68a'}`,
                borderRadius:4, fontSize:10, fontWeight:600,
                color: assumptionsComplete ? '#166534' : '#92400e',
                cursor: !existingQuote?.id ? 'not-allowed' : 'pointer',
                opacity: !existingQuote?.id ? 0.55 : 1,
              }}>
              {assumptionsComplete ? '✓' : '⚠'} Customer Story
            </button>
          }
        />
      </div>

      {/* ── RIGHT: Combined summary ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#f8fafc', minWidth:0 }}>
        <ReviewBanner
          quote={{ ...existingQuote, status: quoteStatus, hubspot_deal_id: hubDealId }}
          quoteType="bundle"
          onStatusChange={s => setQuoteStatus(s)}
        />
        {/* ── Price Lock Banner ── */}
        {pricingSnapshot && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 14px', background:'#1e3a5f', borderBottom:'1px solid #2d4f7a' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:14 }}>🔒</span>
              <div>
                <span style={{ fontSize:11, fontWeight:700, color:'#93c5fd' }}>Prices locked</span>
                <span style={{ fontSize:10, color:'#64748b', marginLeft:6 }}>as of {priceLockDate ? new Date(priceLockDate).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : 'approval'}</span>
              </div>
            </div>
            {(profile?.id === repId || profile?.role === 'admin') && (
              <button onClick={() => setShowUnlockModal(true)}
                style={{ fontSize:10, padding:'3px 10px', background:'rgba(255,255,255,0.1)', color:'#93c5fd', border:'1px solid #2d4f7a', borderRadius:3, cursor:'pointer', fontWeight:600 }}>
                Unlock Pricing
              </button>
            )}
          </div>
        )}
        {showUnlockModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 }}>
            <div style={{ background:'white', borderRadius:10, padding:28, width:440, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:'0 0 10px' }}>⚠ Unlock Pricing</h3>
              <p style={{ fontSize:12, color:'#374151', lineHeight:1.6, margin:'0 0 14px' }}>Pricing was locked when this quote was approved. Unlocking allows rates to update from current package pricing.</p>
              <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:5, padding:'8px 12px', fontSize:11, color:'#991b1b', marginBottom:18 }}>
                If this quote has been sent or exported to Smart Pricing Table, unlocking may cause price discrepancies. Consider a new revision instead.
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button onClick={() => setShowUnlockModal(false)} style={{ padding:'7px 16px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:12, cursor:'pointer' }}>Cancel</button>
                <button onClick={async () => {
                  setPricingSnapshot(null); setPriceLockDate(null); setShowUnlockModal(false);
                  if (existingQuote?.id) await supabase.from('quotes').update({ pricing_snapshot: null, price_locked_at: null, price_locked_by: null }).eq('id', existingQuote.id);
                }} style={{ padding:'7px 18px', background:'#dc2626', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer' }}>Unlock & Recalculate</button>
              </div>
            </div>
          </div>
        )}

        {/* ── v3.5.83.2.1: Lock-Rates-First Banner ──
            Shows when the bundle is saved but OOS rates aren't locked yet.
            Suppressed once pricingSnapshot exists. */}
        {existingQuote?.id && !pricingSnapshot && !ratesLocked && (
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'8px 14px', background:'#fef3c7', borderBottom:'1px solid #fbbf24',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:14 }}>⚠️</span>
              <div>
                <span style={{ fontSize:11, fontWeight:700, color:'#92400e' }}>Lock OOS rates before sending</span>
                <span style={{ fontSize:10, color:'#78350f', marginLeft:6 }}>
                  Click <strong>Accept All Rates</strong> on the Out-of-Scope Rate Schedule card to capture current pricing for this client&rsquo;s market. Required before Bundle Service Agreement generation.
                </span>
              </div>
            </div>
            <button onClick={scrollToOosRateCard}
              style={{ fontSize:10, padding:'4px 10px', background:'#92400e', color:'white', border:'none', borderRadius:3, cursor:'pointer', fontWeight:600 }}>
              Jump to OOS Rates ↓
            </button>
          </div>
        )}

        {/* ── v3.5.83.2.1: Lock-Rates-First Confirmation Modal ──
            Triggered by BundleSignatureCard's onLockRatesPrompt callback when
            the rep clicks Send while rates aren't locked. Three actions:
              - "Lock Rates Now"  → close + scroll to MarketRateCard
              - "Send Anyway"     → close + signatureCardRef.openSendModal()
              - "Cancel"          → just close */}
        {showLockRatesModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 }}>
            <div style={{ background:'white', borderRadius:10, padding:28, width:480, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#92400e', margin:'0 0 10px' }}>⚠ Lock OOS Rates First?</h3>
              <p style={{ fontSize:12, color:'#374151', lineHeight:1.6, margin:'0 0 14px' }}>
                The Out-of-Scope Rate Schedule isn&rsquo;t locked yet. The Bundle Service Agreement can&rsquo;t be generated until OOS rates are locked.
              </p>
              <div style={{ background:'#fef3c7', border:'1px solid #fbbf24', borderRadius:5, padding:'10px 12px', fontSize:11, color:'#78350f', marginBottom:18, lineHeight:1.5 }}>
                <strong>Recommended:</strong> click <em>Lock Rates Now</em>, scroll to the Out-of-Scope Rate Schedule card, and click <em>Accept All Rates</em>. Then return here and click Send again.
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', flexWrap:'wrap' }}>
                <button onClick={() => setShowLockRatesModal(null)}
                  style={{ padding:'7px 14px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:12, cursor:'pointer' }}>
                  Cancel
                </button>
                <button onClick={() => {
                  // "Send Anyway" — see VoiceQuotePage for the same pattern.
                  setShowLockRatesModal(null);
                  signatureCardRef.current?.openSendModal();
                }} style={{ padding:'7px 14px', background:'#fee2e2', border:'1px solid #fca5a5', color:'#991b1b', borderRadius:5, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  Send Anyway
                </button>
                <button onClick={() => {
                  setShowLockRatesModal(null);
                  setTimeout(scrollToOosRateCard, 50);
                }} style={{ padding:'7px 18px', background:'#0f1e3c', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  Lock Rates Now ↓
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ flex:1, overflowY:'auto', padding:'14px 16px' }}>
        <div className="fade-in">
          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                <div style={{ padding:'2px 8px', background:'#4f46e5', color:'white', borderRadius:3, fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Bundle</div>
                <h2 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:0 }}>{proposalName || recipientBiz || 'Bundle Quote'}</h2>
              </div>
              <div style={{ fontSize:10, color:'#6b7280' }}>
                {selectedPkg?.name} + {v.quoteType === 'hosted' ? 'Hosted Voice' : v.quoteType === 'hybrid' ? 'Hybrid Voice' : 'SIP Trunking'} · {contractTerm}-month term
                {bundle && <span style={{ color:'#7c3aed', marginLeft:4, fontWeight:600 }}>· {Math.round(bundle.rate*100)}% voice bundle discount</span>}
              </div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:8, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.04em' }}>Combined MRR</div>
              <div style={{ fontSize:22, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#0f1e3c' }}>{fmt$0(combinedMRR)}</div>
            </div>
          </div>

          {/* KPI cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6, marginBottom:10 }}>
            {[
              ['IT MRR',       fmt$0(itResult?.finalMRR||0),     '#2563eb','#eff6ff'],
              ['Voice MRR',    fmt$0(voiceDiscountedMRR),        '#7c3aed','#faf5ff'],
              ['Bundle Saving',fmt$0(bundle?.voiceDiscount||0),  '#166534','#dcfce7'],
              ['Combined MRR', fmt$0(combinedMRR),               '#0f1e3c','#f0f4ff'],
              ['Implied GM',   fmtPct(combinedGM),               gmColor(combinedGM),gmBg(combinedGM)],
            ].map(([l,val,co,bg])=>(
              <div key={l} style={{ background:bg, borderRadius:5, padding:'6px 5px', textAlign:'center' }}>
                <div style={{ fontSize:7, fontWeight:600, color:'#6b7280', letterSpacing:'.04em', textTransform:'uppercase', marginBottom:2 }}>{l}</div>
                <div style={{ fontSize:12, fontWeight:700, fontFamily:'DM Mono, monospace', color:co }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Bundle discount banner */}
          {bundle?.qualifies && (
            <div style={{ marginBottom:10, padding:'10px 14px', background:'#f0f4ff', border:'1px solid #c4b5fd', borderRadius:7 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:'#4c1d95' }}>📦 Bundle Discount Applied</div>
                  <div style={{ fontSize:10, color:'#7c3aed', marginTop:2 }}>
                    {contractTerm}-month Managed IT commitment → <strong>{Math.round(bundle.rate*100)}% off voice MRR</strong>
                    {/* v3.5.86.2.2: badge text reads the actual threshold from
                        settings rather than hardcoding "$750/mo". The redundant
                        `itBaseMRR >= 750` guard removed — `freePhones` already
                        incorporates the settings-driven check. */}
                    {freePhones && <span style={{ color:'#166534', marginLeft:6 }}>· <strong>Free phones</strong> (IT base ≥ {fmt$0(parseFloat(settings?.bundle_free_phones_min_it_mrr) || 750)}/mo)</span>}
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:8, color:'#9ca3af' }}>Monthly savings</div>
                  <div style={{ fontSize:16, fontWeight:700, fontFamily:'DM Mono, monospace', color:'#166534' }}>{fmt$0(bundle.voiceDiscount)}/mo</div>
                  <div style={{ fontSize:9, color:'#9ca3af' }}>{fmt$0(bundle.voiceDiscount * contractTerm)} over term</div>
                </div>
              </div>
              {/* v3.5.86.2.2: nudge banner for "you're at the qualifying term
                  but your IT MRR is below the free-phones threshold." Latent
                  bug fix: the prior condition `bundle.freePhones && itBaseMRR < 750`
                  was contradictory — bundle.freePhones already requires
                  itBaseMRR >= the threshold, so the nudge never displayed.
                  Corrected condition: contract term hits the free-phones
                  minimum, but IT MRR is below the threshold. Threshold
                  values now read from settings (bundle_free_phones_min_term
                  and bundle_free_phones_min_it_mrr). */}
              {(() => {
                const minTerm  = parseInt(settings?.bundle_free_phones_min_term, 10);
                const minMrr   = parseFloat(settings?.bundle_free_phones_min_it_mrr);
                const minTermR = Number.isFinite(minTerm) ? minTerm : 36;
                const minMrrR  = Number.isFinite(minMrr) ? minMrr : 750;
                const termQualifies = contractTerm >= minTermR;
                const mrrShort      = itBaseMRR < minMrrR;
                if (!termQualifies || !mrrShort) return null;
                return (
                  <div style={{ marginTop:6, fontSize:9, color:'#92400e', background:'#fef3c7', padding:'3px 7px', borderRadius:3 }}>
                    ⚡ Free phones require IT base MRR ≥ {fmt$0(minMrrR)}/mo. Current: {fmt$0(itBaseMRR)}/mo. Add {fmt$0(minMrrR - itBaseMRR)}/mo more to qualify.
                  </div>
                );
              })()}
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, alignItems:'start' }}>

            {/* ── LEFT COLUMN: IT → Voice → Cost Model ── */}
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>

                            {/* Flex Time Meter */}
              <FlexTimeMeter
                pkg={selectedPkg}
                workstations={itInputs?.workstations || 0}
                users={itInputs?.users || 0}
                addonHours={null}
              />

{/* IT line items */}
              <div style={{ background:'white', borderRadius:6, border:'1px solid #e5e7eb', padding:11 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#2563eb', marginBottom:6 }}>🖥 Managed IT — {fmt$0(itResult?.finalMRR||0)}/mo</div>
                {itResult ? (
                  <>
                    <SH l="Base Package"/>
                    <LI lbl={`WS (${itInputs.workstations} × $${selectedPkg?.ws_rate})`} v={itResult.wB} ind/>
                    <LI lbl={`US (${itInputs.users} × $${selectedPkg?.user_rate})`} v={itResult.uB} ind/>
                    <LI lbl="Servers" v={itResult.sB} ind/>
                    <LI lbl="Locations" v={itResult.lB} ind/>
                    <LI lbl="Tenants / Vendors" v={itResult.tB + itResult.vB} ind/>
                    {/* v3.5.61: Log Storage & SIEM Uplift — see QuotePage for
                        rationale; same data shape from itResult. */}
                    {itResult.eB > 0 && (() => {
                      const xEp = itResult.xEp || 0;
                      const xEpStr = xEp % 1 === 0 ? xEp.toFixed(0) : xEp.toFixed(1);
                      return (
                        <LI lbl={`Log Storage & SIEM (${xEpStr} dev × $${itResult.epRate} — ${itResult.epTier})`} v={itResult.eB} ind/>
                      );
                    })()}
                    <LI lbl="IT Base Subtotal" v={itResult.itSubtotal} bold/>
                    {itResult.lineItems.length > 0 && (
                      <>
                        <SH l="Security & Add-ons"/>
                        {itResult.lineItems.map(li => <LI key={li.product_id} lbl={`${li.product_name} (${li.qty} × $${li.sell_price})`} v={li.revenue} ind/>)}
                      </>
                    )}
                    {/* v3.5.58: ITSO Advisory line — appears when Threat Profile
                        drives a non-zero uplift. itsoMRR is post-discount net,
                        so what's shown matches what the customer actually pays. */}
                    {itResult.itsoMRR > 0 && itResult.itsoLabel && (
                      <>
                        <SH l="Strategic Advisory"/>
                        <LI lbl={`${itResult.itsoLabel} (${((itResult.itsoUpliftRate || 0) * 100).toFixed(0)}% × labor base)`} v={itResult.itsoMRR} ind/>
                      </>
                    )}
                    {itResult.discount < 0 && <LI lbl={`${Math.round(itResult.discRate*100)}% contract discount`} v={itResult.discount} ind/>}
                    <LI lbl="✦ IT Final MRR" v={itResult.finalMRR} hi/>
                  </>
                ) : <div style={{ fontSize:11, color:'#9ca3af', padding:'8px 0' }}>Fill in IT details to see pricing</div>}
              </div>

              {/* Voice line items */}
              <div style={{ background:'white', borderRadius:6, border:'1px solid #e5e7eb', padding:11 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#7c3aed', marginBottom:6 }}>📞 Voice — {fmt$0(voiceDiscountedMRR)}/mo</div>
                {voiceResultFinal ? (
                  <>
                    {[['seats','Billable Seats'],['devices','Non-Billable (Included)'],['hybrid','3CX & Hosting'],['sip','SIP Trunking'],['numbers','Numbers & DIDs'],['fax','Fax'],['addons','Add-ons'],['sms','SMS/MMS'],['hardware','Hardware'],['onetime','One-Time Fees']].map(([sec,secLabel])=>{
                      const secLines = voiceResultFinal.lines.filter(l=>l.section===sec);
                      if (!secLines.length) return null;
                      return (
                        <span key={sec}>
                          <SH l={secLabel}/>
                          {secLines.map((l,i)=>(
                            <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'1px 2px', marginLeft:7 }}>
                              <span style={{ fontSize:8, color:l.mrr===0&&!l.nrc?'#9ca3af':'#6b7280' }}>{l.label}</span>
                              <div style={{ textAlign:'right', flexShrink:0, marginLeft:4 }}>
                                {l.mrr!==0&&<div style={{ fontSize:9, fontFamily:'DM Mono, monospace', color:'#374151' }}>{l.mrr<0?`(${fmt$(-l.mrr)})`:fmt$(l.mrr)}<span style={{ fontSize:7, color:'#9ca3af' }}>/mo</span></div>}
                                {l.nrc>0&&<div style={{ fontSize:8, fontFamily:'DM Mono, monospace', color:'#0f766e' }}>{fmt$(l.nrc)}<span style={{ fontSize:7, color:'#9ca3af' }}> NRC</span></div>}
                                {l.mrr===0&&!l.nrc&&<div style={{ fontSize:7, color:'#9ca3af' }}>{l.note||'Incl.'}</div>}
                              </div>
                            </div>
                          ))}
                        </span>
                      );
                    })}
                    <LI lbl="Voice Subtotal" v={voiceResultFinal.finalMRR} bold/>
                    {bundle?.voiceDiscount > 0 && <LI lbl={`Bundle discount (${Math.round(bundle.rate*100)}%)`} v={-bundle.voiceDiscount} ind/>}
                    <LI lbl="✦ Voice Final MRR" v={voiceDiscountedMRR} hi/>
                  </>
                ) : <div style={{ fontSize:11, color:'#9ca3af', padding:'8px 0' }}>Fill in Voice details to see pricing</div>}
              </div>

              {/* Cost model */}
              <div style={{ background:'white', borderRadius:6, border:'1px solid #e5e7eb', padding:11 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6 }}>Combined Cost Model — Monthly Recurring</div>
                {/* v3.5.39 / v3.5.41 / v3.5.57: per-tool tooling breakdown.
                    Legacy aggregate path was removed in v3.5.57 once every
                    package migrated to cost components. */}
                {itResult?.toolingCost > 0 && (
                  <>
                    <LI lbl="IT — Tooling / stack" v={itResult.toolingCost} bold/>
                    {Array.isArray(itResult.toolingTools) && itResult.toolingTools.length > 0 && (
                      <>
                        {itResult.toolingTools
                          .filter(t => t.total > 0 || t.qty > 0)
                          .map((tool, i) => (
                            <LI key={i}
                              lbl={`  ${tool.name} (${tool.qty.toFixed(tool.qty < 10 ? 2 : 0)} × ${fmt$(tool.rate)})`}
                              v={tool.total} ind muted/>
                          ))
                        }
                        <div style={{ fontSize: 8, color: '#9ca3af', fontStyle: 'italic', marginTop: 1, marginBottom: 4, paddingLeft: 8 }}>
                          ↳ from {itResult.toolingTools.length} cost components
                        </div>
                      </>
                    )}
                  </>
                )}
                {itResult?.svcCost > 0 && (
                  <LI lbl={`IT — Labor (${itResult.svcHrs.toFixed(1)} hrs × ${fmt$(burdenedRate)}/hr)`} v={itResult.svcCost} ind/>
                )}
                {itResult?.addonCost > 0 && <LI lbl="IT — Add-on delivery" v={itResult.addonCost} ind/>}
                {itResult?.includedItemsCost > 0 && <LI lbl="IT — Package includes" v={itResult.includedItemsCost} ind/>}
                {/* v3.5.58: ITSO Advisory senior labor cost — visible whenever
                    Threat Profile > None drives an uplift. Hours/cost derived
                    from itsoMRR ÷ senior burdened rate (separate setting from
                    the blended burdened_hourly_rate). */}
                {itResult?.itsoMRR > 0 && itResult?.itsoLabel && (
                  <LI lbl={`${itResult.itsoLabel} labor (${itResult.itsoHours.toFixed(1)} hrs × ${fmt$(getSeniorBurdenedRate(settings))}/hr cost rate)`} v={itResult.itsoCost} ind/>
                )}
                {flexBlock && <LI lbl={`Flex Time labor (${flexBlock.hours}hrs × ${fmt$(burdenedRate)}/hr)`} v={flexLaborCost} ind/>}
                <LI lbl="Voice delivery cost" v={voiceResultFinal?.totalCost||0} ind/>
                <LI lbl="Total Estimated Cost" v={combinedCost} bold/>
                <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 6px', background:gb, borderRadius:4, marginTop:4 }}>
                  <span style={{ fontSize:10, fontWeight:700, color:gc }}>Combined Gross Margin</span>
                  <span style={{ fontSize:13, fontWeight:700, fontFamily:'DM Mono, monospace', color:gc }}>{fmtPct(combinedGM)}</span>
                </div>
                {/* v3.5.39: explicit footnote — onboarding never enters GM */}
                <div style={{ fontSize:8, color:'#9ca3af', marginTop:3, fontStyle:'italic' }}>
                  Excludes onboarding and one-time fees — recurring revenue and recurring cost only
                </div>
                {combinedGM < 0.40 && <div style={{ marginTop:4, fontSize:9, color:'#92400e', background:'#fef3c7', padding:'3px 5px', borderRadius:3 }}>⚠ Below 40% — review scope.</div>}
                <div style={{ marginTop:8, padding:'6px 8px', background:'#f9fafb', borderRadius:4, border:'1px dashed #e5e7eb' }}>
                  <div style={{ fontSize:9, fontWeight:700, color:'#0f766e', marginBottom:3, textTransform:'uppercase', letterSpacing:'.05em' }}>One-Time Fees · NOT in GM</div>
                  <LI lbl="IT Onboarding" v={itResult?.onboarding||0} ind/>
                  {voiceResultFinal?.nrc > 0 && <LI lbl="Voice One-Time Fees" v={voiceResultFinal.nrc} ind/>}
                  <LI lbl="Total One-Time" v={combinedNRC} bold/>
                </div>
              </div>

              {/* Market Rate Analysis */}
              {/* v3.5.83.2.1: id="oos-rate-card" anchor for the Lock-Rates-First
                  guard's "Lock Rates Now" modal action and banner button. */}
              <div id="oos-rate-card">
              <MarketRateCard
                quoteId={existingQuote?.id}
                clientZip={clientZip}
                fallbackMarket={selectedMkt}
                onRatesAccepted={(rates, suggestedTier, analysis) => {
                  if (rates) setAcceptedRates(rates);
                  if (suggestedTier && marketTiers.length) {
                    const tier = marketTiers.find(t => t.tier_key === suggestedTier);
                    if (tier) setSelectedMkt(tier);
                  }
                  // v3.5.38: capture MI's recommended multiplier so the rep can
                  // accept it. Mirrors QuotePage's pattern.
                  if (analysis?.pricing_multiplier != null) {
                    setPendingMultiplier({
                      multiplier: analysis.pricing_multiplier,
                      tier: analysis.market_tier,
                      city: analysis.city,
                      state: analysis.state,
                    });
                    setShowMktRecommend(true);
                  }
                  // v3.5.43: keep marketAnalysis in sync so the rate sheet
                  // reflects the just-accepted rates immediately.
                  if (analysis) {
                    setMarketAnalysis({
                      ...analysis,
                      rates: rates || analysis.rates,
                    });
                  }
                }}
              />
              </div>
            </div>

            {/* ── RIGHT COLUMN: Deal Summary → QuoteNotes → QuoteHistory ── */}
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>

              {/* Combined Deal Summary */}
              <div style={{ background:'#0f1e3c', borderRadius:6, padding:12 }}>
                <div style={{ fontSize:9, fontWeight:700, color:'#475569', letterSpacing:'.06em', textTransform:'uppercase', marginBottom:8 }}>Combined Deal Summary</div>
                {[
                  ['Quote #',        existingQuote?.quote_number || 'Unsaved'],
                  ['Client',         recipientBiz],
                  recipientContact && ['Contact', recipientContact],
                  ['Package',        selectedPkg?.name],
                  ['Contract',       `${contractTerm} months`],
                  ['IT MRR',         fmt$0(itResult?.finalMRR||0)],
                  ['Voice MRR',      fmt$0(voiceDiscountedMRR)],
                  bundle?.voiceDiscount>0 && ['Bundle Savings', `${fmt$0(bundle.voiceDiscount)}/mo`],
                  ['Combined MRR',   fmt$0(combinedMRR)],
                  ['One-Time Fees',  fmt$0(combinedNRC)],
                  ['Total Contract Value', fmt$0(combinedTCV)],
                  hubDealId && ['HubSpot', hubDealName||`#${hubDealId}`],
                ].filter(Boolean).map(([k,val])=>(
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', borderBottom:'1px solid #1e3a5f' }}>
                    <span style={{ fontSize:9, color:'#64748b' }}>{k}</span>
                    <span style={{ fontSize:9, fontWeight:600, color:'white', fontFamily:(typeof val==='string'&&val.startsWith('$'))?'DM Mono, monospace':'inherit' }}>{val}</span>
                  </div>
                ))}
              </div>

              {/* Onboarding Incentive */}
              {/* v3.5.92.2 §4a: picker base narrowed from combinedNRC (which
                  lumped IT onboarding + ALL voice NRC including phones,
                  porting, 10DLC) to bundleOnboardingLabor (IT onboarding +
                  Voice Programming & Configuration only). Voice hardware,
                  porting, and activation NRC flow through the Stripe total
                  unchanged. Customer-facing pricing change on Bundle quotes
                  with active incentives — reps should re-confirm effective
                  NRC with customers between deploy and next save. */}
              {bundleOnboardingLabor > 0 && (
                <OnboardingIncentive
                  fee={bundleOnboardingLabor}
                  marketTier={({ major_metro:'premium', mid_market:'standard', small_market:'adjusted', rural:'secondary' }[zipResult?.tier] || zipResult?.tier || 'secondary')}
                  contractTerm={contractTerm}
                  initialValue={obIncentive}
                  onChange={inc => {
                    setObIncentive(inc);
                    // v3.5.76: parity with QuotePage v3.5.73 — write a version
                    // snapshot to quote_versions on every incentive change so
                    // the activity log captures the rep's choice (including
                    // the No-Incentive case, where mode='none' post-v3.5.75).
                    if (existingQuote?.id) {
                      const label = inc?.mode === 'split'
                        ? `Onboarding: split over ${inc.splitMonths} months (+${fmt$0(inc.monthlyAdd)}/mo)`
                        : inc?.mode === 'discount'
                          ? (inc.discountPct === 100
                              ? `Onboarding: full waive applied — effective fee $0`
                              : `Onboarding: ${inc.discountPct}% discount applied — effective fee ${fmt$0(inc.effectiveFee)}`)
                          : 'Onboarding: no incentive selected — full fee applies';
                      saveQuoteVersion({ quoteId: existingQuote.id, quoteData: { client_name: recipientBiz, status: quoteStatus }, inputs: { proposalName, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc, addendums, package_name: selectedPkg?.name, it: itInputs, voice: v }, totals: { finalMRR: combinedMRR, onboarding: inc?.effectiveFee ?? combinedNRC }, lineItems: [], profile, note: label });
                    }
                  }}
                />
              )}

              {/* v3.5.99.7.23: Manual discount lines (% or $/mo + note) */}
              <ManualDiscounts
                value={manualDiscounts}
                onChange={setManualDiscounts}
                baseMRR={combinedMRRPreDiscount}
              />
              {manualDiscountResolved.total > 0 && (() => {
                const targetGM = parseFloat(settings?.target_gross_margin);
                if (!isFinite(targetGM) || combinedGM >= targetGM) return null;
                return (
                  <div style={{ marginTop: 6, fontSize: 10, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '5px 8px' }}>
                    ⚠ Manual discount drops gross margin below target ({fmtPct(combinedGM)} &lt; {fmtPct(targetGM)}). You can still send — review scope if this wasn&rsquo;t intended.
                  </div>
                );
              })()}

              {/* v3.7.8: one-time (non-recurring) discounts — targeted per NRC
                  category (Onboarding / Hardware / Activation / Porting). */}
              <OneTimeDiscounts
                value={oneTimeDiscounts}
                onChange={setOneTimeDiscounts}
                bases={oneTimeBases}
              />

              {/* v3.5.99.7.24: optional back-dated agreement effective date */}
              <AgreementEffectiveDate value={effectiveDate} onChange={setEffectiveDate} />

              {/* v3.5.71: Bundle signature card — sends Bundle Service Agreement
                  for client signature (combined IT + Voice). Visible whenever
                  the quote has a saved id; gating to 'approved' status is
                  inside the card itself. Sits above DocumentsPanel which
                  renders the Stripe Prepayment card for the same combined
                  upfront NRC amount. */}
              {existingQuote?.id && (
                <SignatureCard docType="bundle_quote"
                  ref={signatureCardRef}
                  quoteId={existingQuote.id}
                  quoteStatus={quoteStatus}
                  quoteNumber={existingQuote?.quote_number}
                  clientName={recipientBiz}
                  clientContact={recipientContact}
                  clientEmail={recipientEmail}
                  upfrontAmount={itResult ? oneTimeResolved.finalNRC : 0}
                  userId={profile?.id}
                  ratesLocked={ratesLocked}
                  onLockRatesPrompt={() => setShowLockRatesModal('send_signature')}
                />
              )}

              {/* Documents — v3.5.43: marketAnalysis from rate sheet (locked)
                  or live zip lookup. Replaces broken zipResult-based synthesis
                  that always returned rates: {} since marketTiers has no
                  rates column. */}
              <DocumentsPanel
                analysis={marketAnalysis || (zipResult ? { city: marketCity, state: marketState, market_tier: zipResult.tier, pricing_multiplier: 1, rates: {} } : null)}
                settings={settings}
                clientName={recipientBiz}
                recipientContact={recipientContact}
                quoteId={existingQuote?.id}
                quoteNumber={existingQuote?.quote_number}
                sptProposalId={sptProposalId}
                onSPTLinked={(pid) => setSptProposalId(pid)}
                inputs={itInputs}
                pkg={selectedPkg}
                products={products}
                complianceKey={(({ hipaa:['hipaa'], soc2:['soc2'], pci:['pci'], cmmc:['cmmc'], moderate:['hipaa','soc2'], high:['pci','cmmc'] })[itInputs?.compliance] || [])}
                result={itResult ? { finalMRR: bundle?.finalMRR || 0, onboarding: itResult.onboarding + (voiceResultFinal?.nrc || 0) } : null}
                obIncentive={obIncentive}
                quoteType="bundle"
                clientEmail={recipientEmail}
                prepayAmount={
                  // v3.5.81.9: Stripe prepayAmount respects the onboarding
                  // incentive. v3.7.8: now the single authoritative net one-time
                  // figure — post-incentive full NRC (IT onboarding + Voice
                  // Programming + hardware + activation + porting) less any
                  // one-time discounts. Matches the Bundle Service Order's
                  // "Total Due Upon Signing" and buildBundleTotals.netNRC.
                  oneTimeResolved.finalNRC
                }
                hubspotDealId={hubDealId}
                /* v3.5.86.3.2: page-driven Customer Story integration. See
                   QuotePage's mount for the full rationale. */
                onOpenAssumptions={() => setShowAssumptions(true)}
              onOpenAddendums={() => setAddendumsModalOpen(true)}
              addendumsCount={addendums.length}
              onOpenServiceOrderPreview={() => setSoPreviewModalOpen(true)}
                assumptionsSaved={assumptionsComplete}
              />

              {/* v3.5.99.6: client engagement panel — auto-hides on quotes with no proposal/SO activity yet */}
              <EngagementPanel quoteId={existingQuote?.id} />
              {/* v3.5.99.7.13: ETF Calculator — visible only on saved quotes
                  (existingQuote?.id present means the quote was loaded/saved). */}
              {existingQuote?.id && (
                <button onClick={() => setShowEtfModal(true)}
                  style={{ width:'100%', marginBottom:10, padding:'8px 12px', background:'white', border:'1px solid #e5e7eb', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600, color:'#0f1e3c', textAlign:'left', display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:14 }}>💰</span>
                  <span style={{ flex:1 }}>Calculate Early Termination Fee</span>
                  <span style={{ fontSize:10, color:'#9ca3af', fontWeight:400 }}>→</span>
                </button>
              )}
              {showEtfModal && (
                <ETFCalculatorModal
                  onClose={() => setShowEtfModal(false)}
                  defaultMRR={combinedMRR || ''}
                  defaultTerm={contractTerm || 36}
                  defaultClientName={recipientBiz}
                  defaultClientAddress={recipientAddress}
                  defaultClientContact={recipientContact}
                  defaultClientEmail={recipientEmail}
                  defaultContractReference={existingQuote?.quote_number || ''}
                  quoteId={existingQuote?.id}
                  hubDealId={hubDealId}
                />
              )}

              {/* Quote Notes */}
              <QuoteNotes
                quoteId={existingQuote?.id}
                quoteNumber={existingQuote?.quote_number}
                clientName={recipientBiz}
                hubDealId={hubDealId}
              />

              {/* Revision History */}
              <QuoteHistory quoteId={existingQuote?.id} />

            </div>
          </div>
        </div>
        </div>{/* end inner scroll */}
      </div>

    </div>

    {/* v3.5.86.3: Customer Story & Assumptions modal mount.
        - quoteType="bundle" routes the AI to the combined IT+Voice prompt.
        - inputs combines IT-side (users, workstations, contractTerm, compliance,
          industryRisk, selectedProducts) with voice-side (voiceTier, voiceSeats,
          voiceDids, voiceFaxType) so the AI has the full picture without the
          modal having to re-capture quote inputs.
        - selectedProductObjects unions IT and voice products so solutionGroups
          can span both surfaces. */}
    {showAssumptions && (
      <AssumptionsModal
        quoteType="bundle"
        onClose={() => setShowAssumptions(false)}
        quoteId={existingQuote?.id}
        quoteNumber={existingQuote?.quote_number}
        clientName={recipientBiz}
        recipientContact={recipientContact}
        inputs={{
          // IT-side aggregate counts and config
          users:           itInputs.users,
          workstations:    itInputs.workstations,
          contractTerm:    itInputs.contractTerm,
          compliance:      itInputs.compliance,
          industryRisk:    itInputs.industryRisk,
          selectedProducts: itInputs.selectedProducts,
          // Voice-side context — derived voice tier label from v.quoteType +
          // v.licenseType so the AI sees a human-readable tier rather than
          // a code. Mirrors the line on the right-side preview header.
          voiceTier:    v.quoteType === 'hosted'
                         ? `Hosted Voice · ${v.licenseType === 'enterprise' ? 'Enterprise' : 'Pro'}`
                         : v.quoteType === 'hybrid' ? 'Hybrid Voice (BYOPBX)'
                         : 'SIP Trunking Only',
          voiceSeats:   v.seats,
          voiceDids:    v.faxDIDs,
          voiceFaxType: v.faxType,
        }}
        pkg={selectedPkg}
        products={products}
        settings={settings}
        complianceKey={(({ hipaa:['hipaa'], soc2:['soc2'], pci:['pci'], cmmc:['cmmc'], moderate:['hipaa','soc2'], high:['pci','cmmc'] })[itInputs?.compliance] || [])}
        onSave={(a) => {
          setAssumptionsStory(a.story || '');
          setAssumptionsSavedAt(a.savedAt || null);
        }}
      />
    )}

      {/* v3.5.99.4.2: AddendumsModal mount — correct location inside BundleQuotePage
          where addendums state lives. Previously v3.5.99.4 mistakenly placed this
          inside CollapsibleSec helper which had no access to the state variables. */}
      <AddendumsModal
        open={addendumsModalOpen}
        onClose={() => setAddendumsModalOpen(false)}
        addendums={addendums}
        templates={addendumTemplates}
        onSave={setAddendums}
      />
      {/* v3.5.99.5: Service Order preview modal — controlled by the Preview button
          on the Documents card. docType is hardcoded per quote page since each
          page knows its own quote type. */}
      <ServiceOrderPreviewModal
        open={soPreviewModalOpen}
        onClose={() => setSoPreviewModalOpen(false)}
        quoteId={existingQuote?.id}
        docType="bundle_quote"
        clientBiz={recipientBiz}
      />
    </>
  );
}

// ─── Collapsible section wrapper ──────────────────────────────────────────────
function CollapsibleSec({ title, open, onToggle, badge, color, children }) {
  return (
    <div style={{ marginBottom:10, border:`1px solid ${open?color+'40':'#e5e7eb'}`, borderRadius:6, overflow:'hidden' }}>
      <div onClick={onToggle} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 10px', background:open?color+'10':'#f9fafb', cursor:'pointer' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ width:2, height:14, background:color, borderRadius:2 }}/>
          <span style={{ fontSize:11, fontWeight:700, color:open?color:'#374151' }}>{title}</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {badge && <span style={{ fontSize:10, fontFamily:'DM Mono, monospace', fontWeight:700, color:color }}>{badge}</span>}
          <span style={{ fontSize:11, color:'#9ca3af' }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && <div style={{ padding:'10px 12px' }}>{children}</div>}
    </div>
  );
}

// ─── UI helpers ───────────────────────────────────────────────────────────────
function Sec({t,c,children}){return(<div style={{marginBottom:10}}><div style={{display:'flex',alignItems:'center',gap:4,marginBottom:5,paddingBottom:3,borderBottom:'1px solid #f1f5f9'}}><div style={{width:2,height:11,background:c||'#2563eb',borderRadius:2}}/><span style={{fontSize:9,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',color:'#6b7280'}}>{t}</span></div>{children}</div>);}
function Grid2({children}){return<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4}}>{children}</div>;}
function Fld({lbl,sub,children,s}){return(<div style={{marginBottom:4,...s}}><label style={{display:'block',fontSize:9,fontWeight:600,color:'#374151',marginBottom:1}}>{lbl}{sub&&<span style={{fontWeight:400,color:'#9ca3af',marginLeft:3,fontSize:9}}>{sub}</span>}</label>{children}</div>);}
function TI({value,onChange,placeholder}){return<input value={value||''} onChange={e=>onChange(e.target.value)} placeholder={placeholder||''} style={{width:'100%',padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,outline:'none'}}/>;}
function NI({v,s}){return<input type="number" value={v} min={0} onChange={e=>s(+e.target.value)} style={{width:'100%',padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,fontFamily:'DM Mono, monospace',color:'#1e3a5f',background:'#eff6ff',fontWeight:600,outline:'none'}}/>;}
function SI({v,s,opts}){return<select value={v} onChange={e=>s(e.target.value)} style={{width:'100%',padding:'4px 6px',border:'1px solid #d1d5db',borderRadius:4,fontSize:11,background:'white',outline:'none',color:'#374151'}}>{opts.map(([a,b])=><option key={a} value={a}>{b}</option>)}</select>;}
function Tog({on,set,lbl,sub}){return(<div onClick={()=>set(!on)} style={{display:'flex',alignItems:'center',gap:7,padding:'5px 7px',borderRadius:4,cursor:'pointer',border:`1px solid ${on?'#93c5fd':'#e5e7eb'}`,background:on?'#eff6ff':'white',marginBottom:2}}><div style={{width:24,height:14,borderRadius:7,flexShrink:0,background:on?'#2563eb':'#d1d5db',position:'relative'}}><div style={{position:'absolute',top:2,left:on?12:2,width:10,height:10,borderRadius:'50%',background:'white',transition:'left .12s'}}/></div><div><span style={{fontSize:10,fontWeight:600,color:on?'#1e40af':'#374151'}}>{lbl}</span>{sub&&<span style={{fontSize:9,color:'#9ca3af',marginLeft:4}}>{sub}</span>}</div></div>);}
function LI({lbl,v,ind,bold,hi,muted}){if(v===0&&!bold&&!hi)return null;return(<div style={{display:'flex',justifyContent:'space-between',padding:hi?'5px 7px':'1px 2px',marginLeft:ind?7:0,borderRadius:hi?4:0,background:hi?'#dcfce7':'transparent',borderTop:bold&&!hi?'1px solid #f3f4f6':'none',marginTop:bold&&!hi?2:0}}><span style={{fontSize:hi?9:8,fontWeight:bold||hi?700:400,color:hi?'#166534':muted?'#9ca3af':bold?'#374151':'#6b7280'}}>{lbl}</span><span style={{fontSize:hi?11:9,fontWeight:bold||hi?700:500,fontFamily:'DM Mono, monospace',color:hi?'#166534':v<0?'#dc2626':bold?'#111827':'#374151'}}>{v<0?`(${fmt$(-v)})`:fmt$(v)}</span></div>);}
function SH({l}){return<div style={{fontSize:7,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'#9ca3af',padding:'3px 2px 1px',marginTop:2}}>{l}</div>;}
